// =============================================================================
// MOBILE DAL WIZARD - Aligné sur le document DAL officiel TEF
// Zones: 10 pièces + étape Besoins
// Structure par zone: Informations → Travaux → Plan 2D IA → Photos
// =============================================================================
import { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronLeft, ChevronRight, Camera, X, Check, Home, Bath,
  Droplets, SunDim, DoorOpen, Save, Wand2,
  Upload, Trash2, CheckCircle2, Circle, Loader2, Sparkles,
  User, Sofa, BedDouble, Warehouse, TreePine, ArrowDownUp,
  MapPin, Phone, Grid, FileText, ScanLine, AlertCircle,
  Target, Plus, Download, Shapes, Accessibility, LayoutGrid, Map as MapIcon,
  Menu as MenuIcon
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { getClientAge, formatAdresse } from "../../utils/clientFormat";
import {
  buildInitialZonesData, buildInitialBesoins, buildInitialIntervenant,
  getSelectedZoneIds, buildStepList, getNextStepKey, getPrevStepKey,
  mergeZonesConfig, zonesConfigSignature, getDalAuthToken,
  buildSolutionsMappingFromZones, buildZonesEvalueesFromZonesData,
  mapLookupVisiteursResponse,
} from "./dalState";
import usePatchDAL from "../../hooks/usePatchDAL";
import ZoneSelectionScreen from "./ZoneSelectionScreen";
import MicButton from "./MicButton";
import JournalVisite from "./JournalVisite";
import DalInfosStep from "./DalInfosStep";
// AG-02 — panneau d'audit IA du DAL (verdict + complétude + incohérences),
// réutilisé tel quel depuis la Vue classique (O1, 0 recréation). Lecture seule.
import { DALAgentPanel } from "../tef/DALAgentPanel";
import Plan2DGenerator from "./Plan2DGenerator";
import { compressImage } from "../../utils/compressImage";
import { enteteTenant } from "../../utils/enteteTenant";
import { tenantResolu } from "../../utils/tenantResolu";

const API = process.env.REACT_APP_BACKEND_URL;

// 10 zones alignées sur le document DAL officiel
// Ordre : Intérieur (SDB, Sanitaires, Volets, Entrée, Salon, Chambre, Escalier) → Extérieur
const ZONES_CONFIG = [
  {
    id: "salle_de_bain", name: "Salle de bain", icon: Bath, color: "blue", critical: true,
    fields: [
      { id: "largeur", label: "Largeur (cm)", type: "number", required: true },
      { id: "longueur", label: "Longueur (cm)", type: "number", required: true },
      { id: "type_sol", label: "Type de sol actuel", type: "text" },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "douche_pmr", label: "Remplacement baignoire par douche PMR", default: true },
      { id: "barres_appui", label: "Barres d'appui", default: true },
      { id: "siege_douche", label: "Siège de douche", default: true },
      { id: "robinetterie", label: "Robinetterie thermostatique", default: true },
      { id: "sol_antiderapant", label: "Sol antidérapant", default: false },
      { id: "lavabo_pmr", label: "Lavabo PMR adapté", default: false },
      { id: "miroir_inclinable", label: "Miroir inclinable", default: false }
    ]
  },
  {
    id: "sanitaires", name: "Sanitaires (WC)", icon: Droplets, color: "purple",
    fields: [
      { id: "largeur", label: "Largeur (cm)", type: "number" },
      { id: "longueur", label: "Longueur (cm)", type: "number" },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "wc_rehausse", label: "WC rehaussé", default: true },
      { id: "barres_appui_wc", label: "Barres d'appui WC", default: true },
      { id: "lave_mains", label: "Lave-mains adapté", default: false },
      { id: "porte_coulissante", label: "Porte coulissante", default: false }
    ]
  },
  {
    id: "volets", name: "Volets", icon: SunDim, color: "orange",
    fields: [
      { id: "nb_volets", label: "Nombre de volets à motoriser", type: "number" },
      { id: "pieces", label: "Pièces concernées", type: "text", placeholder: "Ex: Chambre, Salon" },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "motorisation", label: "Motorisation volets", default: true },
      { id: "telecommande", label: "Télécommande centralisée", default: false }
    ]
  },
  {
    id: "entree", name: "Entrée / Couloirs", icon: Home, color: "slate",
    fields: [
      { id: "largeur_couloir", label: "Largeur couloir (cm)", type: "number" },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "elargissement_couloir", label: "Élargissement couloir", default: false },
      { id: "main_courante_int", label: "Main courante intérieure", default: false },
      { id: "eclairage_auto", label: "Éclairage automatique", default: false },
      { id: "revetement_sol", label: "Revêtement sol antidérapant", default: false }
    ]
  },
  {
    id: "salon", name: "Salon / Cuisine", icon: Sofa, color: "amber",
    fields: [
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "adaptation_cuisine", label: "Adaptation plan de travail", default: false },
      { id: "robinet_adapte", label: "Robinetterie adaptée", default: false },
      { id: "rangements_bas", label: "Rangements accessibles", default: false }
    ]
  },
  {
    id: "chambre", name: "Chambre", icon: BedDouble, color: "indigo",
    fields: [
      { id: "etage", label: "Étage", type: "select", options: ["RDC", "1er étage", "2ème étage", "Autre"] },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "lit_medicalise", label: "Lit médicalisé / adapté", default: false },
      { id: "leve_personne", label: "Lève-personne / rail", default: false },
      { id: "eclairage_nuit", label: "Éclairage nocturne", default: false }
    ]
  },
  {
    id: "escalier", name: "Escalier", icon: ArrowDownUp, color: "green",
    fields: [
      { id: "type_escalier", label: "Type d'escalier", type: "select", options: ["Droit", "Tournant", "Colimaçon"] },
      { id: "nb_marches", label: "Nombre de marches", type: "number" },
      { id: "largeur_escalier", label: "Largeur (cm)", type: "number" },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "monte_escalier_droit", label: "Monte-escalier droit", default: false },
      { id: "monte_escalier_tournant", label: "Monte-escalier tournant", default: false },
      { id: "plateforme", label: "Plateforme élévatrice", default: false },
      { id: "main_courante", label: "Main courante renforcée", default: true }
    ]
  },
  {
    id: "exterieur", name: "Extérieur / Accès", icon: DoorOpen, color: "red",
    fields: [
      { id: "type_acces", label: "Type d'accès", type: "select", options: ["Plain-pied", "Marches", "Escalier extérieur"] },
      { id: "nb_marches_ext", label: "Nombre de marches", type: "number" },
      { id: "largeur_passage", label: "Largeur passage (cm)", type: "number" },
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "rampe_acces", label: "Rampe d'accès PMR", default: true },
      { id: "main_courante_ext", label: "Main courante extérieure", default: true },
      { id: "eclairage_ext", label: "Éclairage extérieur adapté", default: false },
      { id: "elargissement_porte", label: "Élargissement porte d'entrée", default: false },
      { id: "seuil_pmr", label: "Seuil PMR", default: false }
    ]
  },
  {
    id: "garage", name: "Garage / Cave", icon: Warehouse, color: "stone",
    fields: [
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "eclairage", label: "Éclairage adapté", default: false },
      { id: "acces_securise", label: "Accès sécurisé", default: false }
    ]
  },
  {
    id: "balcon", name: "Balcon / Terrasse", icon: TreePine, color: "emerald",
    fields: [
      { id: "observations", label: "Observations", type: "textarea" }
    ],
    travaux: [
      { id: "garde_corps", label: "Garde-corps adapté", default: false },
      { id: "sol_antiderapant_ext", label: "Sol antidérapant", default: false },
      { id: "seuil_abaisse", label: "Seuil abaissé", default: false }
    ]
  }
];

// Groupes de zones : Intérieur / Extérieur
// Ordre Intérieur : SDB → Sanitaires → Volets puis reste
const ZONE_GROUPS = [
  {
    id: "interieur", name: "Intérieur",
    zones: ["salle_de_bain", "sanitaires", "volets", "entree", "salon", "chambre", "escalier"]
  },
  {
    id: "exterieur_group", name: "Extérieur",
    zones: ["exterieur", "garage", "balcon"]
  }
];

// ═══ Styles charte OmniHub — dual-mode via tokens var(--oh-*) ═══
// (cf. docs/upload/CHARTE-GRAPHIQUE-OMNIHUB.md). Un SEUL accent par écran :
// les zones ne portent plus une palette décorative — état actif = accent,
// état inactif = surface neutre. La sémantique zone reste portée par l'icône +
// le libellé, pas par la couleur.
const pairOf = (bg, tx, bd) => ({ background: `var(${bg})`, color: `var(${tx})`, borderColor: `var(${bd})` });
const successStyle = pairOf("--oh-success-bg", "--oh-success-text", "--oh-success-border");
const errorStyle = pairOf("--oh-error-bg", "--oh-error-text", "--oh-error-border");
const infoStyle = pairOf("--oh-info-bg", "--oh-info-text", "--oh-info-border");
// Mappe les anciens noms de couleur d'état (green/red/amber/blue/orange) vers
// les 3 paires sémantiques de la charte.
const stateOf = (c) => (c === "green" ? successStyle : c === "red" ? errorStyle : infoStyle);
const cardStyle = { background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" };
const surfaceStyle = { background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" };
const inputStyle = { background: "var(--oh-bg-elevated)", color: "var(--oh-text)", borderColor: "var(--oh-border)" };
const btnPrimary = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const btnSecondary = { background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" };
const btnDiscreet = { background: "transparent", border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" };
const menuActive = { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" };
const menuIdle = { background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)", borderColor: "transparent" };
const accentTint = { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" };
const accentText = { color: "var(--oh-accent-text)" };
const zoneActive = { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" };
const zoneIdle = { background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)", borderColor: "transparent" };
const txtMain = { color: "var(--oh-text)" };
const txtSec = { color: "var(--oh-text-secondary)" };
const txtMuted = { color: "var(--oh-text-muted)" };

// Types de documents (mêmes que version PC)
const MOBILE_DOCUMENT_TYPES = [
  { value: "piece_identite", label: "Carte d'identité", icon: "id" },
  { value: "avis_imposition", label: "Avis d'imposition", icon: "tax" },
  { value: "justificatif_domicile", label: "Justificatif de domicile", icon: "home" },
  { value: "rib", label: "RIB", icon: "bank" },
  { value: "attestation_honneur", label: "Attestation sur l'honneur", icon: "doc" },
  { value: "rapport_visite", label: "Rapport de visite technique", icon: "doc" },
  { value: "plan_financement", label: "Plan de financement", icon: "doc" },
  { value: "autre", label: "Autre document", icon: "doc" },
];

// Indicateur status PATCH per-field (idle | saving | saved | error)
function PatchStatusBadge({ status, lastSavedAt, errorMsg }) {
  if (status === "idle") return null;
  const fmt = lastSavedAt ? lastSavedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
  if (status === "saving") {
    return <span className="text-[10px] flex items-center gap-1" style={{ color: "var(--oh-accent-contrast)", opacity: 0.85 }} data-testid="patch-status-saving">
      <Loader2 className="w-2.5 h-2.5 animate-spin" /> Sauvegarde…
    </span>;
  }
  if (status === "saved") {
    return <span className="text-[10px] flex items-center gap-1" style={{ color: "var(--oh-accent-contrast)", opacity: 0.85 }} data-testid="patch-status-saved">
      <Check className="w-2.5 h-2.5" /> Sauvegardé {fmt}
    </span>;
  }
  if (status === "error") {
    return <span className="text-[10px] flex items-center gap-1" style={{ color: "var(--oh-error-text)" }} data-testid="patch-status-error" title={errorMsg || ""}>
      <AlertCircle className="w-2.5 h-2.5" /> Échec sauvegarde
    </span>;
  }
  return null;
}

// Bouton IA Transcrire / Rédiger.
// Réutilisé pour : (1) Besoins — transcription dictée → français pro,
// (2) Solutions — « Rédiger IA » : mot-clé court → préconisation rédigée
// argumentée (même endpoint /dal/transcribe, rédacteur ANAH/MaPrimeAdapt').
// `label`/`testId` optionnels pour adapter le libellé et cibler chaque ligne
// sans casser les usages Besoins existants.
function AITranscribeBtn({ value, onResult, context, dossierId, label = "Transcrire", testId = "ai-transcribe-btn" }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    if (!value?.trim()) return;
    setLoading(true);
    try {
      const token = getDalAuthToken();
      const res = await fetch(`${API}/api/metier/tef/dal/transcribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...enteteTenant(tenantResolu()), ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ text: value, context, dossier_id: dossierId }),
      });
      if (res.ok) { const d = await res.json(); if (d.result) onResult(d.result); }
    } catch (e) { console.error("Transcription err:", e); }
    finally { setLoading(false); }
  };
  if (!value?.trim()) return null;
  return (
    <button type="button" onClick={handle} disabled={loading}
      className="flex items-center gap-1 font-medium text-xs px-2.5 py-1 rounded-md border disabled:opacity-60" style={btnSecondary} data-testid={testId}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {label}
    </button>
  );
}

// Import d'un DAL PDF existant (T2) — porté de l'ancien DALForm vers l'étape
// Documents du wizard. Auth mobile via getDalAuthToken (mobile_dal_token ||
// session). Endpoint backend POST .../dal/import-pdf inchangé.
// ⚠️ NOTE SÉCU (tracée, NON traitée ici) : cette route lit X-Tenant-ID de façon
// spoofable (IDOR same-user cross-tenant, routes.py). Non aggravée par ce port.
function ImportDALPDFBlockMobile({ dossierId, tenantId, dalImportedPdf, onImported }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [clientMatch, setClientMatch] = useState(null);
  const [applying, setApplying] = useState(false);
  const [appliedMsg, setAppliedMsg] = useState("");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    if (!(file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""))) {
      setErr("Sélectionnez un fichier PDF"); e.target.value = ""; return;
    }
    setUploading(true);
    try {
      const token = getDalAuthToken();
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nom", `DAL importé — ${file.name}`);
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/import-pdf`, {
        method: "POST",
        headers: { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) },
        credentials: "include",
        body: fd,
      });
      if (res.ok) {
        const d = await res.json();
        onImported?.(d.dal_imported_pdf);
        setExtracted(d.extracted || null);
        setClientMatch(d.client_match || null);
        setAppliedMsg("");
      }
      else { const j = await res.json().catch(() => ({})); setErr(j.detail || `Erreur import (HTTP ${res.status})`); }
    } catch (e2) { setErr(`Erreur réseau : ${e2?.message || e2}`); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Point 2 — pré-remplissage EN CONFIRMATION (approche A, non destructif).
  const applyExtracted = async () => {
    if (!extracted) return;
    setApplying(true); setErr("");
    try {
      const token = getDalAuthToken();
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/apply-extracted`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) },
        credentials: "include",
        body: JSON.stringify({ extracted }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setAppliedMsg(`✓ ${d.applied_count || 0} champ(s) appliqué(s) au dossier. Rechargez le DAL pour les voir.`); setExtracted(null); }
      else setErr(d.detail || `Erreur application (HTTP ${res.status})`);
    } catch (e2) { setErr(`Erreur réseau : ${e2?.message || e2}`); }
    finally { setApplying(false); }
  };

  const detected = extracted ? (() => {
    const b = extracted.beneficiaire || {}, l = extracted.logement || {}, be = extracted.besoins || {};
    const vals = [...Object.values(b), ...Object.values(l), ...Object.values(be)].filter(v => v != null && v !== "");
    return { count: vals.length, zones: (extracted.zones || []).length, benef: `${b.prenom || ""} ${b.nom || ""}`.trim() };
  })() : null;
  const pair = stateOf;

  return (
    <div className="p-3 rounded-lg border" style={surfaceStyle} data-testid="import-dal-pdf-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={accentText} />
          {/* FT4 — libellé net : « Import DAL » = extraction/pré-remplissage (à ne pas
              confondre avec « Lier DAL » = rattacher un PDF existant, cf. DALConsultation). */}
          <span className="text-sm font-medium" style={txtMain}>Import DAL</span>
        </div>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={handleUpload} className="hidden" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm"
          variant="outline" style={btnSecondary} data-testid="import-dal-pdf-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {dalImportedPdf ? "Remplacer le PDF importé" : "Import DAL"}
        </Button>
      </div>
      {err && <p className="text-[11px] mt-2" style={{ color: "var(--oh-error-text)" }}>{err}</p>}
      {!dalImportedPdf && !err && (
        <p className="text-xs mt-2" style={txtMuted}>Upload → Zoho 02_PIECES_DU_DOSSIER.</p>
      )}
      {dalImportedPdf && (
        <div className="mt-2 text-xs flex flex-wrap items-center gap-2" style={txtSec} data-testid="import-dal-pdf-done">
          <CheckCircle2 className="w-3 h-3" style={{ color: "var(--oh-success-text)" }} />
          <span>{dalImportedPdf.nom || dalImportedPdf.filename}</span>
          {dalImportedPdf.document_id && (
            <a href={`${API}/api/metier/documents/${dalImportedPdf.document_id}/download`} target="_blank" rel="noreferrer"
               className="underline inline-flex items-center gap-1" style={accentText}>
              <Download className="w-3 h-3" /> Télécharger
            </a>
          )}
        </div>
      )}

      {/* Point 2 — Alerte contrôle client (le PDF ne correspond pas au dossier) */}
      {clientMatch && clientMatch.ok === false && (
        <div className="mt-2 rounded-lg border p-2 text-xs" style={pair("red")} data-testid="import-client-mismatch">
          ⚠ Le PDF importé ne correspond pas au client du dossier — PDF : « {clientMatch.extracted_name} » / dossier : « {clientMatch.dossier_name} ». Vérifiez avant d'appliquer.
        </div>
      )}

      {/* Point 2 — Aperçu des champs détectés + application EN CONFIRMATION (non destructif) */}
      {extracted && detected && (detected.count > 0 || detected.zones > 0) && (
        <div className="mt-2 rounded-lg border p-3" style={pair("blue")} data-testid="import-extracted-preview">
          <div className="text-xs font-medium mb-1">Contenu détecté dans le PDF</div>
          <div className="text-[11px] opacity-90">
            {detected.benef && <span>Bénéficiaire : {detected.benef} · </span>}
            {detected.count} champ(s) · {detected.zones} zone(s) détectée(s).
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button onClick={applyExtracted} disabled={applying} size="sm" variant="outline" style={btnSecondary} data-testid="import-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Appliquer au dossier (champs vides)
            </Button>
            <Button onClick={() => setExtracted(null)} disabled={applying} size="sm" variant="ghost" style={txtSec} data-testid="import-ignore-btn">
              Ignorer
            </Button>
          </div>
          <p className="text-[10px] opacity-70 mt-1">Non destructif : seuls les champs actuellement vides sont remplis. Vous validez.</p>
        </div>
      )}
      {appliedMsg && (
        <div className="mt-2 rounded-lg border p-2 text-xs" style={pair("green")} data-testid="import-applied-msg">{appliedMsg}</div>
      )}
    </div>
  );
}

// Composant Plan 2D IA simplifié pour mobile
function MobilePlan2DAI({ zoneName, zoneId, dossierId, dimensions }) {
  const [description, setDescription] = useState("");
  const [generating, setGenerating] = useState(false);
  const [planImage, setPlanImage] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importedUrl, setImportedUrl] = useState(null);
  const [importErr, setImportErr] = useState("");

  // Stopgap : import manuel d'un plan 2D / croquis (image ou PDF) attaché au dossier.
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportErr("");
    const ok = (file.type || "").startsWith("image/") || file.type === "application/pdf"
      || /\.(png|jpe?g|webp|pdf)$/i.test(file.name || "");
    if (!ok) { setImportErr("Image ou PDF uniquement"); e.target.value = ""; return; }
    setImporting(true);
    try {
      const token = getDalAuthToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/dal/upload-plan-2d/${dossierId}`, {
        method: "POST",
        headers: { ...enteteTenant(tenantResolu()), ...(token && { Authorization: `Bearer ${token}` }) },
        body: fd,
      });
      if (res.ok) { const d = await res.json(); setImportedUrl(d.url); }
      else { setImportErr("Échec de l'import"); }
    } catch (err) { setImportErr("Erreur réseau"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const generate = async () => {
    if (!description.trim()) return;
    setGenerating(true);
    try {
      const token = getDalAuthToken();
      const res = await fetch(`${API}/api/dal/generate-plan-2d-ai`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...enteteTenant(tenantResolu()), ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ description, zone_name: zoneName, dimensions, dossier_id: dossierId }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.image_base64) setPlanImage(`data:image/png;base64,${d.image_base64}`);
      }
    } catch (e) { console.error("Plan 2D err:", e); }
    finally { setGenerating(false); }
  };

  return (
    <section data-testid={`plan-2d-${zoneId}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <Label className="text-sm flex items-center gap-2" style={txtSec}><Grid className="w-4 h-4" style={accentText} /> Plan 2D (IA)</Label>
        <div className="flex items-center gap-1">
          <MicButton value={description} onTranscript={v => setDescription(v)} />
          <AITranscribeBtn value={description} onResult={v => setDescription(v)} context="description d'aménagement PMR d'une pièce pour générer un plan 2D (diagnostic autonomie)" dossierId={dossierId} label="Améliorer IA" testId={`plan2d-desc-ia-${zoneId}`} />
        </div>
      </div>
      {/* CC-17 Option B (2026-05-17) — pièces non rectangulaires non supportées par l'auto */}
      <p className="mb-2 text-[11px] leading-tight" style={txtMuted}
         data-testid={`plan2d-shape-warning-${zoneId}`}>
        Pièce rectangulaire uniquement. Pour pièces en L / T / décrochements,
        utiliser le mode manuel côté desktop (éditeur Konva).
      </p>
      <Textarea value={description} onChange={e => setDescription(e.target.value)}
        placeholder={`Décrivez l'aménagement souhaité pour ${zoneName}...\nEx: Douche italienne à gauche, lavabo PMR en face de la porte, WC rehaussé à droite`}
        className="min-h-[70px] text-sm" style={inputStyle} />
      <Button onClick={generate} disabled={generating || !description.trim()}
        variant="outline" className="w-full mt-2" style={btnSecondary} size="sm" data-testid={`generate-plan-${zoneId}`}>
        {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération...</> : <><Wand2 className="w-4 h-4 mr-2" />Générer le plan IA</>}
      </Button>
      <label className="w-full mt-2 flex items-center justify-center gap-2 cursor-pointer rounded-md border text-sm py-2" style={btnSecondary}
        data-testid={`import-plan-${zoneId}`}>
        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {importedUrl ? "Remplacer le plan / croquis" : "Importer un plan / croquis"}
        <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleImport} className="hidden" />
      </label>
      {importErr && <p className="mt-1 text-[11px]" style={{ color: "var(--oh-error-text)" }}>{importErr}</p>}
      {importedUrl && (
        <a href={`${API}${importedUrl}`} target="_blank" rel="noreferrer" className="mt-1 block text-[11px] underline" style={accentText}>
          Voir le plan / croquis importé
        </a>
      )}
      {planImage && (
        <div className="mt-2 relative">
          <img src={planImage} alt="Plan 2D IA" className="w-full rounded-lg border-2" style={{ borderColor: "var(--oh-accent)" }} />
          <Badge className="absolute bottom-2 right-2" style={accentTint}>Plan IA</Badge>
        </div>
      )}
    </section>
  );
}

// 🔴 N°702 — `compressImage` VIT MAINTENANT DANS `utils/compressImage.js`.
// ⚠️ Elle était PRIVÉE ici : l'écran de visite de Mario ⛔ ne pouvait pas l'appeler et envoyait
// donc le cliché BRUT (« Mémoire insuffisante », vécu par JJ). Le corps est repris À L'IDENTIQUE
// — ⛔ aucun paramètre changé — donc le comportement du DAL de Jordan est INCHANGÉ.

export default function MobileDALWizard({
  dossierId, tenantId, clientName = "Client", clientInfo = {}, dossierInfo = {},
  onComplete, onCancel, initialData = {}, visiteurAmo = "",
  // CC-AUDIT-43 L2 — `embedded` = rendu desktop dans l'onglet DAL (carte bornée,
  // header/footer non plein-écran). Défaut false → PWA mobile inchangée.
  embedded = false,
}) {
  // CC-AUDIT-43 L1 — source unique de la config zones/travaux = backend
  // (GET /api/dal/zones-config, fetché au montage ci-dessous). La const
  // ZONES_CONFIG locale sert de SEED initial + FALLBACK si l'API échoue
  // (offline-first DAL Mobile). Les métadonnées UI (icônes, couleurs, champs)
  // restent locales — elles ne peuvent pas venir du JSON serveur.
  const [zonesConfig, setZonesConfig] = useState(ZONES_CONFIG);
  const [selectedZoneIds, setSelectedZoneIds] = useState(() => getSelectedZoneIds(initialData, ZONES_CONFIG));
  const steps = buildStepList(selectedZoneIds);
  const [currentStepKey, setCurrentStepKey] = useState("infos");
  // DAL Phase 3 — menu LATÉRAL desktop (embedded uniquement). `activeSection`
  // pilote quel contenu s'affiche à droite. Le stepper mobile (currentStepKey +
  // Précédent/Suivant) reste INCHANGÉ quand !embedded.
  const [activeSection, setActiveSection] = useState("beneficiaire");
  // Charte §9 responsive — menu latéral (embedded) devient un drawer sur mobile (< md).
  const [menuOpen, setMenuOpen] = useState(false);
  const [zonesData, setZonesData] = useState(() =>
    buildInitialZonesData(initialData, ZONES_CONFIG, dossierInfo?.logement || {})
  );
  const [besoins, setBesoins] = useState(() => buildInitialBesoins(initialData, clientInfo));
  const [intervenant, setIntervenant] = useState(() => buildInitialIntervenant(initialData, visiteurAmo));
  // BUG-05 : surface habitable saisissable depuis le wizard si manquante en DB.
  // Bloque l'avancée vers les zones tant qu'elle n'est pas renseignée.
  const [surfaceHabitable, setSurfaceHabitable] = useState(() => {
    // lit tous les chemins connus (surface saisie dans « Infos Logement » =
    // logement.surface_habitable, ou legacy infos_logement / dossier).
    const s = dossierInfo?.surface_habitable
      ?? initialData?.logement?.surface_habitable
      ?? initialData?.infos_logement?.surface_habitable;
    return s != null && s !== "" ? String(s) : "";
  });
  const [surfaceError, setSurfaceError] = useState(null);

  // Dérivé de la config courante (suit le backend après fetch) — whitelist
  // client mirror pour usePatchDAL.
  const ZONE_IDS_SET = useMemo(() => new Set(zonesConfig.map(z => z.id)), [zonesConfig]);
  const { patch: patchField, status: patchStatus, lastSavedAt: patchSavedAt, errorMsg: patchError } = usePatchDAL({
    dossierId, tenantId, zoneIds: ZONE_IDS_SET,
  });
  const [visiteurs, setVisiteurs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docsChecklist, setDocsChecklist] = useState(null);
  const [docsChecklistLoading, setDocsChecklistLoading] = useState(false);
  const [docsChecklistError, setDocsChecklistError] = useState(null);
  const [docDirectUploading, setDocDirectUploading] = useState(null);
  const docDirectFileRef = useRef(null);
  const docDirectTypeRef = useRef(null);
  const [saving, setSaving] = useState(false);
  // C1b — mapping risques ANAH ↔ solutions AMO (repris du DALForm desktop).
  // Reprise d'un mapping déjà saisi (initialData.solutions_mapping) sinon vide ;
  // pré-rempli à l'entrée de l'étape « solutions » depuis les zones (non destructif).
  const [solutions, setSolutions] = useState(() =>
    Array.isArray(initialData?.solutions_mapping) ? initialData.solutions_mapping : []
  );
  // C1a — génération PDF DAL déclenchée depuis handleFinish (après save).
  const [pdfGenerating, setPdfGenerating] = useState(false);
  // T3 — Télécharger PERSISTANT : si un DAL a déjà été généré (dal_genere exposé
  // par mobile-auth / GET .../dal), on initialise pdfResult au montage pour que le
  // bouton Télécharger s'affiche AU CHARGEMENT (pas seulement après Générer).
  const [pdfResult, setPdfResult] = useState(() => (
    initialData?.dal_genere
      ? {
          download_url: initialData.download_url || `/api/metier/tef/dossiers/${dossierId}/dal/download`,
          filename: initialData.dal_pdf_filename || `DAL_${dossierId}.pdf`,
          persisted: true,
        }
      : null
  )); // {download_url, filename}
  // T2 — import DAL PDF existant (repris de infos_dal.dal_imported_pdf).
  const [dalImportedPdf, setDalImportedPdf] = useState(() => initialData?.dal_imported_pdf || null);
  const [pdfError, setPdfError] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  // CC-AUDIT-43 L3 (restauré 2026-07-01) — éditeur Konva avancé OPTIONNEL (pièces
  // en L, équipements draggables). Hors flux Précédent/Suivant : panneau repliable
  // par zone. JAMAIS une étape forcée. Persistance = endpoint dossier-level réutilisé.
  const [showKonvaEditor, setShowKonvaEditor] = useState(false);
  const [plan2dEditorMsg, setPlan2dEditorMsg] = useState(null);
  // Section Plan 2D dédiée (menu latéral desktop) — repli/déplie l'éditeur avancé par zone.
  const [plan2dOpen, setPlan2dOpen] = useState({});
  // Feedback ACTIONS (menu latéral desktop) — ex. « Enregistré ».
  const [actionMsg, setActionMsg] = useState(null);
  const [zone3dImages, setZone3dImages] = useState({});
  const [generatingZone3d, setGeneratingZone3d] = useState({});
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const docCameraRef = useRef(null);
  const docFileRef = useRef(null);

  // Source unique : valeurs canoniques de la fiche dossier/client (gir, apa,
  // date_visite côté mobile). Si renseignées, l'input correspondant est en
  // lecture seule — le DAL Mobile est un formulaire de visite, pas de saisie
  // initiale. Cf. DALForm desktop pour le pattern complet (7 champs).
  const [canonical, setCanonical] = useState({});
  const isLockedCanonical = (key) => {
    const v = canonical?.[key];
    return v !== null && v !== undefined && v !== "";
  };
  const lockedHint = "🔒 Renseigné depuis la fiche dossier";

  // Load visiteurs list from dossiers lookup
  useEffect(() => {
    const loadVisiteurs = async () => {
      try {
        const token = getDalAuthToken();
        const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
        const res = await fetch(`${API}/api/metier/lookup/visiteurs`, { headers });
        if (res.ok) {
          const data = await res.json();
          // P5 — mapper partagé : passe les objets {nom, prenom, telephone,
          // fonction} tels quels (téléphone conservé pour l'auto-remplissage) et
          // normalise l'ancien format array de strings ("Prénom Nom").
          const mapped = mapLookupVisiteursResponse(data);
          if (mapped.length > 0) setVisiteurs(mapped);
        }
      } catch (e) { console.error("Load visiteurs err:", e); }
    };
    loadVisiteurs();
  }, [tenantId]);

  // Charge les valeurs canoniques (gir/apa/date_visite côté fiche). Même
  // endpoint que DALForm desktop — réponse `data.canonical` contient les 7
  // valeurs courantes côté fiche dossier/client.
  useEffect(() => {
    if (!dossierId) return;
    const loadCanonical = async () => {
      try {
        const token = getDalAuthToken();
        const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.canonical) setCanonical(data.canonical);
        }
      } catch (e) { console.error("Load canonical err:", e); }
    };
    loadCanonical();
  }, [dossierId, tenantId]);

  // CC-AUDIT-43 L1 — fetch de la config zones SERVEUR au montage (source unique).
  // Le backend (dal_zones_config.ZONES_CONFIG) fait foi sur les IDs zones/travaux
  // et leurs libellés ; la const locale reste un fallback. On ne reconstruit
  // zonesData/selectedZoneIds QUE si la signature serveur diffère du local
  // (auto-heal d'une divergence) — cas aligné = no-op, aucun risque pour
  // l'autosave en cours.
  const zonesConfigFetchedRef = useRef(false);
  useEffect(() => {
    if (zonesConfigFetchedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const token = getDalAuthToken();
        const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
        const res = await fetch(`${API}/api/dal/zones-config`, { headers });
        if (!res.ok) return; // garde le fallback local (résilience)
        const data = await res.json();
        const merged = mergeZonesConfig(ZONES_CONFIG, data?.zones);
        if (cancelled || !merged || merged.length === 0) return;
        zonesConfigFetchedRef.current = true;
        if (zonesConfigSignature(merged) !== zonesConfigSignature(ZONES_CONFIG)) {
          // Divergence réelle backend/front → réaligner sur le backend.
          setZonesData(buildInitialZonesData(initialData, merged, dossierInfo?.logement || {}));
          setSelectedZoneIds(getSelectedZoneIds(initialData, merged));
        }
        setZonesConfig(merged);
      } catch { /* réseau KO → fallback local conservé */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Génération 3D par zone
  const generateZone3D = async (zone) => {
    const travaux = zonesData[zone.id].travaux;
    if (!travaux.length) return;
    const preconisations = zone.travaux.filter(t => travaux.includes(t.id)).map(t => t.label);
    if (!preconisations.length) return;
    setGeneratingZone3d(prev => ({ ...prev, [zone.id]: true }));
    try {
      const token = getDalAuthToken();
      const res = await fetch(`${API}/api/dal/generate-zone-3d/${dossierId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ zone_id: zone.id, zone_name: zone.name, preconisations, style: "realistic" })
      });
      if (res.ok) {
        const data = await res.json();
        setZone3dImages(prev => ({ ...prev, [zone.id]: { url: data.image_url, image_base64: data.image_base64 } }));
      }
    } catch (e) { console.error("3D gen error:", e); }
    finally { setGeneratingZone3d(prev => ({ ...prev, [zone.id]: false })); }
  };

  // Steps: "besoins" → "selection" → ...selectedZoneIds → "docs"
  const totalSteps = steps.length;
  const currentStepIndex = Math.max(0, steps.indexOf(currentStepKey));
  const displayStep = currentStepIndex + 1;
  const progress = (displayStep / totalSteps) * 100;

  const currentZone = zonesConfig.find(z => z.id === currentStepKey) || null;
  const isInfosStep = currentStepKey === "infos";
  const isBesoinsStep = currentStepKey === "besoins";
  const isSelectionStep = currentStepKey === "selection";
  const isSolutionsStep = currentStepKey === "solutions";
  const isDocsStep = currentStepKey === "docs";

  const updateField = (zoneId, fieldId, value) => {
    setZonesData(p => ({ ...p, [zoneId]: { ...p[zoneId], fields: { ...p[zoneId].fields, [fieldId]: value } } }));
  };
  const blurField = (zoneId, fieldId, value) => {
    patchField(`zones.${zoneId}.fields.${fieldId}`, value);
  };
  const toggleTravail = (zoneId, travailId) => {
    setZonesData(p => {
      const cur = p[zoneId].travaux || [];
      const nextTravaux = cur.includes(travailId) ? cur.filter(t => t !== travailId) : [...cur, travailId];
      patchField(`zones.${zoneId}.travaux`, nextTravaux);
      return { ...p, [zoneId]: { ...p[zoneId], travaux: nextTravaux } };
    });
  };
  const toggleZone = (zoneId) => {
    setZonesData(p => {
      const next = !p[zoneId].enabled;
      patchField(`zones.${zoneId}.enabled`, next);
      return { ...p, [zoneId]: { ...p[zoneId], enabled: next } };
    });
  };
  const toggleSelectedZone = (zoneId) => {
    setSelectedZoneIds(prev => {
      const set = new Set(prev);
      if (set.has(zoneId)) set.delete(zoneId); else set.add(zoneId);
      // Conserve l'ordre canonique (cf. getSelectedZoneIds)
      const next = zonesConfig.map(z => z.id).filter(id => set.has(id));
      patchField("selected_zones", next);
      return next;
    });
  };
  const handleStartFromSelection = () => {
    // Démarre sur la 1ʳᵉ zone cochée si présente, sinon va aux Docs
    const target = selectedZoneIds.length > 0 ? selectedZoneIds[0] : "docs";
    setCurrentStepKey(target);
  };
  // Wrappers besoins/intervenant : updates state local + patch champ par champ
  const updateBesoin = (key, value) => {
    setBesoins(p => ({ ...p, [key]: value }));
    patchField(`besoins.${key}`, value);
  };

  // Point 4 — Âge AUTO depuis la date de naissance (source unique). On réutilise
  // le helper canonique `getClientAge` (client.age / client.date_naissance) et,
  // à défaut, l'âge MPA dérivé côté serveur depuis date_naissance
  // (`eligibilite_mpa.age`, plumé via dossierInfo.eligibilite_mpa_age). Quand une
  // valeur est disponible → champ en LECTURE SEULE et synchronisé en base ;
  // sinon → saisie manuelle conservée (repli).
  const autoAge = getClientAge(clientInfo) ?? (
    dossierInfo?.eligibilite_mpa_age != null && Number(dossierInfo.eligibilite_mpa_age) > 0
      ? Number(dossierInfo.eligibilite_mpa_age) : null
  );
  useEffect(() => {
    if (autoAge != null && String(besoins.age ?? "") !== String(autoAge)) {
      setBesoins(p => ({ ...p, age: autoAge }));
      patchField("besoins.age", autoAge);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAge]);
  const updateIntervenant = (key, value) => {
    setIntervenant(p => ({ ...p, [key]: value }));
    patchField(`intervenant.${key}`, value);
  };

  // ── C1b — Solutions (mapping risques ANAH ↔ solutions AMO) ──────────────
  // Construit le mapping depuis les zones évaluées du wizard (réutilise la
  // logique partagée buildSolutionsMappingFromZones via une projection client
  // miroir du bridge backend). Source de vérité = préconisations cochées.
  const buildSolutionsFromCurrentZones = () =>
    buildSolutionsMappingFromZones(
      buildZonesEvalueesFromZonesData(zonesData, zonesConfig)
    );
  const regenerateSolutions = () => setSolutions(buildSolutionsFromCurrentZones());
  const updateSolution = (idx, key, value) =>
    setSolutions(prev => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  const addSolution = () =>
    setSolutions(prev => [...prev, { probleme: "", solution: "", zone: "" }]);
  const deleteSolution = (idx) =>
    setSolutions(prev => prev.filter((_, i) => i !== idx));

  // Pré-remplissage à l'entrée de l'étape Solutions : uniquement si vide
  // (non destructif — ne jamais écraser un mapping déjà saisi / repris).
  useEffect(() => {
    if (currentStepKey !== "solutions") return;
    setSolutions(prev => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return buildSolutionsMappingFromZones(
        buildZonesEvalueesFromZonesData(zonesData, zonesConfig)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepKey]);

  // ── Upload immédiat photo (fix P1 2026-05-20 — Jordan terrain Bonicel) ──
  // iOS Safari peut perdre le content d'un File après background/foreground
  // (PWA mobile, switch app, écran verrouillé). Si on attendait `saveDAL`
  // pour uploader, le File arrivait vide → guard 422 → photo perdue.
  // → On upload IMMÉDIATEMENT après compression. Si succès, on stocke
  // `uploaded:true` + server_url et on retire la référence File (libère
  // mémoire). En cas d'échec, on garde le File pour retry au save.
  const uploadPhotoNow = async (file, zoneId) => {
    const token = getDalAuthToken();
    const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
    const fd = new FormData();
    fd.append("file", file);
    fd.append("zone", zoneId);
    const r = await fetch(`${API}/api/dal/upload-photo/${dossierId}`, { method: "POST", headers, body: fd });
    if (!r.ok) {
      let detail = `HTTP ${r.status}`;
      try { const j = await r.json(); detail = j.detail || j.error || detail; } catch {}
      throw new Error(detail);
    }
    return r.json().catch(() => ({}));
  };

  // Photo
  const handlePhotoCapture = async (e) => {
    const files = e.target.files;
    if (!files?.length || !currentZone) return;
    const zoneId = currentZone.id;
    const captured = [];
    for (const rawFile of files) {
      // Compression iPhone (~2-2.5 Mo → < 900KB) pour éviter 413 Nginx silencieux
      const file = await compressImage(rawFile);
      const id = `p_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      captured.push({
        id,
        localUrl: URL.createObjectURL(file),
        file,                              // gardé pour retry si upload immédiat échoue
        originalSize: rawFile.size,
        compressedSize: file.size,
        timestamp: new Date().toISOString(),
        uploaded: false,
        uploading: true,
        uploadError: null,
      });
    }
    setZonesData(p => ({ ...p, [zoneId]: { ...p[zoneId], photos: [...p[zoneId].photos, ...captured] } }));
    e.target.value = "";

    // Upload immédiat (en parallèle pour chaque photo)
    for (const ph of captured) {
      uploadPhotoNow(ph.file, zoneId)
        .then((res) => {
          setZonesData(p => ({
            ...p,
            [zoneId]: {
              ...p[zoneId],
              photos: p[zoneId].photos.map(x =>
                x.id === ph.id
                  ? { ...x, uploaded: true, uploading: false, uploadError: null, server_url: res?.url || res?.file_url, server_id: res?.id || res?.photo_id, file: null }
                  : x
              ),
            },
          }));
        })
        .catch((err) => {
          console.error("[DAL] upload-photo immédiat échec:", err);
          setZonesData(p => ({
            ...p,
            [zoneId]: {
              ...p[zoneId],
              photos: p[zoneId].photos.map(x =>
                x.id === ph.id ? { ...x, uploading: false, uploadError: err.message || "réseau" } : x
              ),
            },
          }));
        });
    }
  };
  const deletePhoto = (zoneId, photoId) => {
    setZonesData(p => ({ ...p, [zoneId]: { ...p[zoneId], photos: p[zoneId].photos.filter(ph => ph.id !== photoId) } }));
  };

  // Documents
  const [selectedDocType, setSelectedDocType] = useState("piece_identite");

  // Checklist documents — pièces ANAH présentes vs manquantes
  // Source : GET /api/dal/mobile/{id}/documents-checklist (même collection
  // metier_documents que la fiche desktop, via AG-03 check_anah_checklist).
  const loadDocsChecklist = async () => {
    if (!dossierId) return;
    setDocsChecklistLoading(true);
    setDocsChecklistError(null);
    try {
      const token = getDalAuthToken();
      const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
      const res = await fetch(`${API}/api/dal/mobile/${dossierId}/documents-checklist`, { headers });
      if (!res.ok) {
        setDocsChecklistError(`HTTP ${res.status}`);
        setDocsChecklist(null);
      } else {
        const data = await res.json();
        if (data?.found === false) {
          setDocsChecklistError(data.error || "Dossier introuvable");
          setDocsChecklist(null);
        } else {
          setDocsChecklist(data);
        }
      }
    } catch (e) {
      setDocsChecklistError(e?.message || "réseau");
      setDocsChecklist(null);
    } finally {
      setDocsChecklistLoading(false);
    }
  };

  // Charger la checklist quand l'utilisateur entre sur l'étape Documents
  useEffect(() => {
    if (currentStepKey === "docs") {
      loadDocsChecklist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepKey]);

  // Upload immédiat d'un manquant : pré-sélectionne le type et ouvre la caméra/picker
  const triggerDirectUploadFor = (typeDocument) => {
    docDirectTypeRef.current = typeDocument;
    docDirectFileRef.current?.click();
  };

  const handleDirectUpload = async (e) => {
    const files = e.target.files;
    const typeDocument = docDirectTypeRef.current;
    e.target.value = "";
    if (!files?.length || !typeDocument) return;
    setDocDirectUploading(typeDocument);
    try {
      const token = getDalAuthToken();
      const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
      for (const rawFile of files) {
        const file = rawFile.type?.startsWith("image/") ? await compressImage(rawFile) : rawFile;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("type_document", typeDocument);
        fd.append("nom", file.name);
        const r = await fetch(`${API}/api/dal/upload-document-mobile/${dossierId}`, { method: "POST", headers, body: fd });
        if (!r.ok) {
          let detail = `${r.status}`;
          try { const j = await r.json(); detail = j.detail || j.error || detail; } catch {}
          throw new Error(detail);
        }
      }
      // Rafraîchir la checklist : la pièce manquante doit basculer en présente
      await loadDocsChecklist();
    } catch (err) {
      setDocsChecklistError(`Upload échoué: ${err.message || err}`);
    } finally {
      setDocDirectUploading(null);
    }
  };

  // Upload immédiat document admin — même rationale que uploadPhotoNow
  const uploadDocNow = async (file, typeDocument, nom) => {
    const token = getDalAuthToken();
    const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type_document", typeDocument);
    fd.append("nom", nom);
    const r = await fetch(`${API}/api/dal/upload-document-mobile/${dossierId}`, { method: "POST", headers, body: fd });
    if (!r.ok) {
      let detail = `HTTP ${r.status}`;
      try { const j = await r.json(); detail = j.detail || j.error || detail; } catch {}
      throw new Error(detail);
    }
    return r.json().catch(() => ({}));
  };

  const handleDocCapture = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const added = [];
    for (const rawFile of files) {
      // Compression images uniquement (pas les PDF)
      const file = rawFile.type?.startsWith("image/") ? await compressImage(rawFile) : rawFile;
      added.push({
        id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        type: selectedDocType,
        typeLabel: MOBILE_DOCUMENT_TYPES.find(t => t.value === selectedDocType)?.label || selectedDocType,
        file,
        localUrl: URL.createObjectURL(file),
        fileName: file.name,
        originalSize: rawFile.size,
        compressedSize: file.size,
        timestamp: new Date().toISOString(),
        uploaded: false,
        uploading: true,
        uploadError: null,
      });
    }
    setDocuments(prev => [...prev, ...added]);
    e.target.value = "";

    // Upload immédiat (parallèle)
    for (const d of added) {
      uploadDocNow(d.file, d.type, d.typeLabel || d.fileName)
        .then((res) => {
          setDocuments(prev => prev.map(x =>
            x.id === d.id
              ? { ...x, uploaded: true, uploading: false, uploadError: null, server_id: res?.id || res?.doc_id, file: null }
              : x
          ));
        })
        .catch((err) => {
          console.error("[DAL] upload-document-mobile immédiat échec:", err);
          setDocuments(prev => prev.map(x =>
            x.id === d.id ? { ...x, uploading: false, uploadError: err.message || "réseau" } : x
          ));
        });
    }
  };
  const deleteDocument = (docId) => {
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  // Save — retourne {ok, stats:{photos, docs, dal}, errors:[]}
  const saveDAL = async () => {
    setSaving(true);
    const errors = [];
    const stats = { photos: { sent: 0, ok: 0 }, docs: { sent: 0, ok: 0 }, dal: false };
    try {
      const token = getDalAuthToken();
      const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };

      // Photos / docs — Fix P1 2026-05-20 : upload désormais EN TEMPS RÉEL
      // dans handlePhotoCapture / handleDocCapture (uploadPhotoNow /
      // uploadDocNow). Au save final, on compte simplement les uploadés
      // OK et on RETRY les échecs si le File est encore valide.
      // Les photos avec `uploaded:true` sont comptées comme OK sans nouvelle
      // requête — évite le problème iOS où le File devient vide entre
      // capture et save (background/foreground).
      for (const zone of zonesConfig) {
        const zd = zonesData[zone.id];
        if (!zd.enabled) continue;
        for (const photo of zd.photos) {
          if (photo.uploaded) {
            stats.photos.ok += 1;
            continue; // déjà uploadé pendant la capture
          }
          if (photo.uploading) {
            errors.push(`Photo ${zone.name} : upload en cours, patientez puis recommencez`);
            continue;
          }
          // Retry : seulement si File toujours valide
          if (!photo.file) {
            errors.push(`Photo ${zone.name} : perdue (re-capturez)`);
            continue;
          }
          const isValidFile =
            (photo.file instanceof File || photo.file instanceof Blob) &&
            photo.file.size > 0 &&
            (photo.file.type || "").startsWith("image/");
          if (!isValidFile) {
            errors.push(`Photo ${zone.name} : fichier vide (re-capturez — iOS peut perdre le File en arrière-plan)`);
            continue;
          }
          stats.photos.sent += 1;
          try {
            const res = await uploadPhotoNow(photo.file, zone.id);
            stats.photos.ok += 1;
            // Marquer comme uploadé pour idempotence
            const _ph = photo;
            setZonesData(p => ({
              ...p,
              [zone.id]: {
                ...p[zone.id],
                photos: p[zone.id].photos.map(x => x.id === _ph.id ? { ...x, uploaded: true, uploading: false, server_url: res?.url || res?.file_url, file: null } : x),
              },
            }));
          } catch (err) {
            errors.push(`Photo ${zone.name} : ${err.message || "réseau"}`);
          }
        }
      }

      // Documents — même logique : skip les déjà-uploadés, retry les échecs
      for (const doc of documents) {
        if (doc.uploaded) {
          stats.docs.ok += 1;
          continue;
        }
        if (doc.uploading) {
          errors.push(`Document ${doc.typeLabel} : upload en cours`);
          continue;
        }
        if (!doc.file) {
          errors.push(`Document ${doc.typeLabel} : perdu (re-uploadez)`);
          continue;
        }
        stats.docs.sent += 1;
        try {
          await uploadDocNow(doc.file, doc.type, doc.typeLabel || doc.fileName);
          stats.docs.ok += 1;
          const _d = doc;
          setDocuments(prev => prev.map(x => x.id === _d.id ? { ...x, uploaded: true, uploading: false, file: null } : x));
        } catch (err) {
          errors.push(`Document ${doc.typeLabel} : ${err.message || "réseau"}`);
        }
      }

      // Sauvegarder les données DAL (étape critique)
      try {
        const r = await fetch(`${API}/api/dal/save-mobile/${dossierId}`, {
          method: "POST", headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            zones: zonesData,
            besoins,
            intervenant,
            // Métadonnées dossier auto-remplies (depuis OH) pour traçabilité côté backend
            dossier_meta: {
              reference: dossierInfo?.reference,
              type_projet: dossierInfo?.type_projet,
              logement: dossierInfo?.logement,
              client_nom: clientName,
            },
            // date_visite : si déjà renseignée en fiche dossier (canonical),
            // on la respecte — sinon timestamp de la visite courante.
            date_visite: canonical?.date_visite || new Date().toISOString(),
            completed: true,
          })
        });
        if (!r.ok) {
          let detail = `${r.status}`;
          try { const j = await r.json(); detail = j.detail || j.error || detail; } catch {}
          errors.push(`Enregistrement DAL: ${detail}`);
        } else {
          stats.dal = true;
        }
      } catch (err) {
        errors.push(`Enregistrement DAL: ${err.message || "réseau"}`);
      }

      // C1b — persister le mapping solutions (risques ANAH ↔ solutions AMO).
      // Le POST /save-mobile n'accepte PAS solutions_mapping ; on passe donc par
      // la route desktop PUT /dossiers/{id}/dal (même contrat que DALForm, qui
      // merge ce champ dans infos_dal — lu par le générateur PDF section
      // « Solutions proposées »). NON BLOQUANT : un échec (ex. token mobile non
      // autorisé sur la route desktop) ne gâte pas `ok` — les préconisations
      // restent persistées via zones_evaluees (bridge save-mobile).
      if (stats.dal) {
        try {
          const rs = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal`, {
            method: "PUT",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ solutions_mapping: solutions }),
          });
          if (!rs.ok) {
            console.warn("[DAL] persist solutions_mapping non-OK:", rs.status);
          }
        } catch (e) {
          console.warn("[DAL] persist solutions_mapping err:", e?.message || e);
        }
      }

      const ok = stats.dal && errors.length === 0;
      return { ok, stats, errors };
    } catch (err) {
      console.error("Save error:", err);
      errors.push(err.message || "erreur inconnue");
      return { ok: false, stats, errors };
    } finally { setSaving(false); }
  };

  // Persistance du plan 2D Konva — RÉUTILISE l'endpoint dossier-level du DAL
  // desktop (DALForm.jsx → POST /api/metier/tef/dossiers/{id}/plan-2d/save).
  // Auth via getDalAuthToken() (mobile_dal_token || session_token).
  const savePlan2D = async (data) => {
    const payload = data?.plan_2d_data;
    if (!payload || !payload.dims) {
      setPlan2dEditorMsg({ type: "error", text: "Plan 2D : données invalides (dimensions manquantes)" });
      return;
    }
    setPlan2dEditorMsg({ type: "saving", text: "Sauvegarde du plan…" });
    try {
      const token = getDalAuthToken();
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/plan-2d/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...enteteTenant(tenantId),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setPlan2dEditorMsg({ type: "saved", text: "Plan 2D sauvegardé" });
      } else {
        const err = await res.json().catch(() => ({}));
        setPlan2dEditorMsg({ type: "error", text: err.detail || `Erreur sauvegarde plan 2D (HTTP ${res.status})` });
      }
    } catch (e) {
      setPlan2dEditorMsg({ type: "error", text: `Erreur réseau : ${e.message}` });
    }
  };

  const goNext = () => {
    // Surface habitable N'EST PLUS bloquante (regroupée dans « Infos Logement »,
    // non bloquante par décision JJ 2026-07-02 — cf. seuil complétude 1+2+3).
    setSurfaceError(null);
    setCurrentStepKey(prev => getNextStepKey(prev, steps));
  };
  const goPrev = () => setCurrentStepKey(prev => getPrevStepKey(prev, steps));
  const [saveError, setSaveError] = useState(null);

  // C1a — génération du PDF DAL. Même contrat d'appel que DALForm.generatePDF :
  // POST /api/metier/tef/dossiers/{id}/dal/generer → {success, filename, download_url}.
  // Le DAL est déjà sauvegardé par saveDAL en amont (pas de re-PUT ici).
  // NE JETTE PAS : on signale l'échec via pdfError, sans bloquer onComplete.
  const generateDalPdf = async () => {
    setPdfError(null);
    setPdfResult(null);
    setPdfGenerating(true);
    try {
      const token = getDalAuthToken();
      const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/generer`, {
        method: "POST", headers,
      });
      if (res.ok) {
        const data = await res.json();
        setPdfResult({ download_url: data.download_url, filename: data.filename });
      } else {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); detail = j.detail || j.error || detail; } catch {}
        setPdfError(detail);
      }
    } catch (e) {
      console.error("[DAL] génération PDF échec:", e);
      setPdfError(e?.message || "réseau");
    } finally {
      setPdfGenerating(false);
    }
  };

  // Téléchargement du PDF généré (endpoint authentifié → fetch + blob,
  // même logique que DALForm).
  const downloadDalPdf = async () => {
    if (!pdfResult?.download_url) return;
    setPdfDownloading(true);
    try {
      const token = getDalAuthToken();
      const headers = { ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) };
      const resp = await fetch(`${API}${pdfResult.download_url}`, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfResult.filename || `DAL_${dossierId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError(`Téléchargement : ${e?.message || e}`);
    } finally {
      setPdfDownloading(false);
    }
  };

  // ── VERROU de génération (TASK 2, testé JJ « généré avec rien = grave ») ──
  // On ne compte QUE les entrées RÉELLEMENT renseignées, pas les zones
  // fantômes/canoniques : buildInitialZonesData active toutes les zones avec
  // leurs travaux par défaut → il faut donc INTERSECTER avec selectedZoneIds
  // (les zones que le visiteur a explicitement cochées à l'écran Sélection).
  const genuineZonesTravaux = useMemo(() => (
    (selectedZoneIds || []).filter(zid => {
      const z = zonesData?.[zid];
      return z?.enabled && Array.isArray(z.travaux) && z.travaux.length > 0;
    }).length
  ), [selectedZoneIds, zonesData]);
  const genuineSolutions = useMemo(() => (
    (solutions || []).filter(s => (s?.solution || "").trim()).length
  ), [solutions]);
  const intervenantOk = !!(intervenant?.nom || "").trim();
  // 3 bloqueurs obligatoires — libellés réutilisés dans le récap + ACTIONS.
  const genBlockers = [
    ...(genuineZonesTravaux > 0 ? [] : ["≥ 1 zone avec travaux"]),
    ...(genuineSolutions > 0 ? [] : ["≥ 1 solution préconisée"]),
    ...(intervenantOk ? [] : ["Intervenant (nom)"]),
  ];
  const genLocked = genBlockers.length > 0;

  // Navigation menu latéral (embedded). Synchronise currentStepKey pour que les
  // effets existants (prefill solutions, chargement checklist docs) se déclenchent
  // et que la section Zones garde une zone active « sous le capot ».
  const selectSection = (id) => {
    setActiveSection(id);
    const stepMap = { beneficiaire: "infos", logement: "infos", handicap: "infos", besoins: "besoins", solutions: "solutions", documents: "docs" };
    if (id === "zones") {
      setCurrentStepKey(prev =>
        (prev === "selection" || zonesConfig.some(z => z.id === prev)) ? prev : (selectedZoneIds[0] || "selection")
      );
    } else if (stepMap[id]) {
      setCurrentStepKey(stepMap[id]);
    }
    // plan2d / ia / actions : ne touchent pas currentStepKey (contenus autonomes).
  };

  // T3 — bouton "Générer le DAL" explicite (dernière étape) : flush la sauvegarde
  // complète (zones/solutions/photos bulk en plus de l'auto-save per-field) PUIS
  // génère le PDF. Ne quitte pas le wizard (contrairement à Terminer/onComplete).
  const handleGenerate = async () => {
    setSaveError(null);
    // VERROU DUR : refuse la génération si un des 3 bloqueurs manque, même si un
    // appelant contourne l'attribut `disabled` (TASK 2 — « ça doit VRAIMENT bloquer »).
    if (genLocked) {
      setPdfError(`Génération bloquée — complétez d'abord : ${genBlockers.join(", ")}.`);
      return;
    }
    const result = await saveDAL();
    if (!result.ok) { setSaveError(result); return; }
    await generateDalPdf();
  };

  const handleFinish = async () => {
    setSaveError(null);
    const result = await saveDAL();
    if (result.ok) {
      // C1a — DAL enregistré : on génère le PDF (non bloquant), puis on
      // notifie le parent. Un échec de génération laisse l'enregistrement
      // valide (pdfError affiché), onComplete est tout de même appelé.
      await generateDalPdf();
      if (onComplete) {
        onComplete({ zones: zonesData, besoins, intervenant, documents, solutions, uploadStats: result.stats });
      }
    } else {
      // Affichage erreurs explicites (bannière rouge)
      setSaveError(result);
    }
  };

  // Adresse visite : priorité logement (source canonique) > client
  const logAdr = dossierInfo?.logement;
  const clientAdr = clientInfo?.adresse;
  // Adresse lisible : on réutilise le helper canonique `formatAdresse` (O1) qui gère
  // les cas objet imbriqué `{adresse:{rue,code_postal,ville}}`, objet adresse direct
  // et string. Le bug « [object Object] » venait de `logement.adresse` (un OBJET)
  // concaténé tel quel. Précédence : logement.adresse > logement (à plat) > client.adresse.
  const addressStr = formatAdresse(logAdr?.adresse) || formatAdresse(logAdr) || formatAdresse(clientAdr);

  // Paires d'état sémantiques (charte) — mappe green/red/amber/blue → succès/erreur/info.
  const pair = stateOf;

  // ── Sélection du contenu affiché — fonctionne dans les DEUX modes ──
  // Mobile (!embedded) : piloté par currentStepKey (stepper inchangé).
  // Desktop (embedded) : piloté par activeSection (menu latéral).
  const emb = embedded;
  const showInfosAll = !emb && isInfosStep;                 // mobile : 3 blocs Infos
  const showInfosBenef = emb && activeSection === "beneficiaire";
  const showInfosLog = emb && activeSection === "logement";
  const showInfosHdcp = emb && activeSection === "handicap";
  const showBesoins = emb ? activeSection === "besoins" : isBesoinsStep;
  const showSelection = emb ? activeSection === "zones" : isSelectionStep;
  const showZoneEval = emb ? (activeSection === "zones" && !!currentZone) : !!currentZone;
  const showSolutions = emb ? activeSection === "solutions" : isSolutionsStep;
  const showDocs = emb ? activeSection === "documents" : isDocsStep;
  const showPlan2d = emb && activeSection === "plan2d";
  const showIaAudit = emb && activeSection === "ia_audit";
  const showIaImport = emb && activeSection === "ia_import";
  const showActions = emb && activeSection === "actions";
  // Dans l'étape Docs : audit IA + bouton Générer présents en MOBILE seulement
  // (desktop → déplacés dans les groupes IA / ACTIONS du menu latéral).
  const docsShowAudit = !emb;
  const docsShowGenerate = !emb;

  // Bloc de messages d'état (save / génération PDF) — réutilisé dans le footer
  // mobile ET dans la vue ACTIONS du menu latéral desktop.
  const statusMessagesEl = (
    <>
      {saveError && (
        <div className="mb-3 p-3 rounded-lg border text-xs" style={errorStyle} data-testid="mobile-dal-save-error">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium mb-1">Échec de l'enregistrement</p>
              <ul className="space-y-0.5 list-disc pl-4">
                {saveError.errors.slice(0, 4).map((err, i) => (
                  <li key={i} className="truncate" title={err}>{err}</li>
                ))}
                {saveError.errors.length > 4 && (
                  <li className="opacity-80">+ {saveError.errors.length - 4} autres erreurs</li>
                )}
              </ul>
              <p className="mt-1 opacity-80">
                Photos : {saveError.stats.photos.ok}/{saveError.stats.photos.sent} OK ·
                Docs : {saveError.stats.docs.ok}/{saveError.stats.docs.sent} OK ·
                DAL : {saveError.stats.dal ? "✓" : "✗"}
              </p>
            </div>
            <button onClick={() => setSaveError(null)} className="p-1 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {pdfGenerating && (
        <div className="mb-3 p-3 rounded-lg border text-xs flex items-center gap-2" style={infoStyle} data-testid="mobile-dal-pdf-generating">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" /> Génération du PDF DAL en cours…
        </div>
      )}
      {pdfResult && !pdfGenerating && (
        <div className="mb-3 p-3 rounded-lg border text-xs flex items-center justify-between gap-2" style={successStyle} data-testid="mobile-dal-pdf-success">
          <span className="flex items-center gap-2 min-w-0">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span className="truncate">PDF DAL généré{pdfResult.filename ? ` — ${pdfResult.filename}` : ""}</span>
          </span>
          <Button
            size="sm" variant="outline"
            onClick={downloadDalPdf}
            disabled={pdfDownloading}
            className="h-8 px-2 text-xs shrink-0" style={btnSecondary}
            data-testid="mobile-dal-pdf-download-btn"
          >
            {pdfDownloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            Télécharger le DAL
          </Button>
        </div>
      )}
      {pdfError && !pdfGenerating && (
        <div className="mb-3 p-3 rounded-lg border text-xs flex items-start gap-2" style={infoStyle} data-testid="mobile-dal-pdf-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span className="flex-1">DAL enregistré, mais la génération du PDF a échoué ({pdfError}). Vous pouvez réessayer.</span>
          <button onClick={generateDalPdf} className="underline shrink-0" data-testid="mobile-dal-pdf-retry">Réessayer</button>
        </div>
      )}
    </>
  );

  // Groupe SECTIONS du menu latéral (desktop). Ordre imposé (TASK 1).
  const MENU_SECTIONS = [
    { id: "beneficiaire", label: "Bénéficiaire", Icon: User },
    { id: "logement", label: "Logement", Icon: Home },
    { id: "handicap", label: "Handicap & autonomie", Icon: Accessibility },
    { id: "besoins", label: "Besoins", Icon: Target },
    { id: "zones", label: "Zones", Icon: LayoutGrid },
    { id: "solutions", label: "Solutions", Icon: Wand2 },
    { id: "plan2d", label: "Plan 2D", Icon: MapIcon },
    { id: "documents", label: "Documents", Icon: FileText },
  ];
  const menuBtnStyle = (on) => (on
    ? { background: "var(--oh-accent-bg)", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" }
    : { background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" });

  // Charte §9 — libellé de la section courante (pour la barre mobile).
  const currentSectionLabel =
    MENU_SECTIONS.find((s) => s.id === activeSection)?.label
    || (activeSection === "ia_audit" ? "Auditer le DAL"
      : activeSection === "ia_import" ? "Import DAL"
      : activeSection === "actions" ? "Actions" : "DAL");

  // Menu DAL — réutilisé par l'aside desktop ET le drawer mobile. `onPick` referme
  // le drawer après sélection. Cibles tactiles ≥44px sur mobile (charte §9).
  const menuItemCls = "w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-left transition-colors min-h-[44px] md:min-h-0";
  const renderDalMenu = (onPick = () => {}) => (
    <div className="space-y-3">
      <div>
        <div className="px-2 pb-1 text-[10px] font-medium uppercase tracking-wider" style={txtMuted}>Sections</div>
        <div className="space-y-1">
          {MENU_SECTIONS.map(({ id, label, Icon }) => {
            const on = activeSection === id;
            return (
              <button key={id} type="button" onClick={() => { selectSection(id); onPick(); }}
                data-testid={`dal-menu-section-${id}`} aria-current={on ? "true" : undefined}
                className={menuItemCls} style={menuBtnStyle(on)}>
                <Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div data-testid="dal-menu-ia">
        <div className="px-2 pb-1 text-[10px] font-medium uppercase tracking-wider" style={txtMuted}>IA</div>
        <div className="space-y-1">
          <button type="button" onClick={() => { selectSection("ia_audit"); onPick(); }}
            data-testid="dal-menu-ia-audit" aria-current={activeSection === "ia_audit" ? "true" : undefined}
            className={menuItemCls} style={menuBtnStyle(activeSection === "ia_audit")}>
            <Sparkles className="w-4 h-4 shrink-0" /> <span className="truncate">Auditer le DAL</span>
          </button>
          <button type="button" onClick={() => { selectSection("ia_import"); onPick(); }}
            data-testid="dal-menu-ia-import" aria-current={activeSection === "ia_import" ? "true" : undefined}
            className={menuItemCls} style={menuBtnStyle(activeSection === "ia_import")}>
            <ScanLine className="w-4 h-4 shrink-0" /> <span className="truncate">Import DAL</span>
          </button>
        </div>
      </div>
      <div data-testid="dal-menu-actions">
        <div className="px-2 pb-1 text-[10px] font-medium uppercase tracking-wider" style={txtMuted}>Actions</div>
        <div className="space-y-1">
          <button type="button" onClick={() => { selectSection("actions"); onPick(); }}
            data-testid="dal-menu-actions-entry" aria-current={activeSection === "actions" ? "true" : undefined}
            className={menuItemCls} style={menuBtnStyle(activeSection === "actions")}>
            <Save className="w-4 h-4 shrink-0" /> <span className="truncate">Actions</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Zones réellement concernées (cochées + activées) — pour la section Plan 2D.
  const planZones = (selectedZoneIds.length ? selectedZoneIds : zonesConfig.map(z => z.id))
    .map(id => zonesConfig.find(z => z.id === id))
    .filter(z => z && zonesData[z.id]?.enabled);

  return (
    <div
      className={
        embedded
          // Desktop : carte qui REMPLIT son conteneur (panneau ~66% du split).
          // Point 3 — on retire `max-w-5xl`/`mx-auto` (héritage PWA mobile) qui
          // bridaient le wizard à 1024px centré ; le panneau parent gère la largeur.
          ? "flex flex-col rounded-xl border overflow-hidden max-h-[85vh] w-full"
          // Mobile : plein écran (PWA terrain) — comportement historique.
          : "min-h-screen flex flex-col"
      }
      style={{ background: "var(--oh-bg)", borderColor: "var(--oh-border)" }}
      data-testid="mobile-dal-wizard"
    >
      {/* Header — FT2 (2026-07-02) : le bandeau client orange a été RETIRÉ (la fiche
          dossier au-dessus affiche déjà client/adresse). Ne reste que la barre de
          progression + un rappel discret d'étape et le statut d'autosave (charte). */}
      <header className={embedded ? "z-40 shrink-0" : "sticky top-0 z-40"}>
        <div className="flex items-center justify-between gap-2 px-4 py-1.5"
             style={{ background: "var(--oh-bg-surface)", borderBottom: "1px solid var(--oh-border)" }}
             data-testid="mobile-dal-header">
          <div className="flex items-center gap-2 min-w-0">
            {/* Charte §9 — burger : ouvre le menu des sections en drawer sur mobile (embedded). */}
            {embedded && (
              <button onClick={() => setMenuOpen(true)}
                className="md:hidden -ml-1 rounded-lg shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px]"
                style={{ color: "var(--oh-text-secondary)" }} data-testid="dal-mobile-burger" aria-label="Ouvrir le menu des sections">
                <MenuIcon className="w-5 h-5" />
              </button>
            )}
            {onCancel && (
              <button onClick={onCancel} className="p-1 rounded-lg shrink-0 hidden md:flex items-center justify-center" style={{ color: "var(--oh-text-secondary)" }} data-testid="mobile-dal-cancel">
                <X className="w-4 h-4" />
              </button>
            )}
            {embedded
              ? <span className="text-sm font-medium truncate" style={{ color: "var(--oh-text)" }} data-testid="dal-current-section">{currentSectionLabel}</span>
              : <span className="text-xs font-medium" style={{ color: "var(--oh-text-secondary)" }}>Étape {displayStep}/{totalSteps}</span>}
          </div>
          <PatchStatusBadge status={patchStatus} lastSavedAt={patchSavedAt} errorMsg={patchError} />
        </div>
        {/* Barre de progression */}
        <div className="h-1.5" style={{ background: "var(--oh-border)" }}>
          <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: "var(--oh-accent-solid)" }} />
        </div>
      </header>

      {/* Charte §9 — drawer mobile du menu des sections (embedded, < md). Le fond
          coupe l'interaction ; sélectionner une section referme le drawer. */}
      {embedded && menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true" data-testid="dal-mobile-drawer">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85%] overflow-y-auto p-3 shadow-xl"
               style={{ background: "var(--oh-bg-surface)", borderRight: "1px solid var(--oh-border)" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Sections du DAL</span>
              <button onClick={() => setMenuOpen(false)}
                className="rounded-lg flex items-center justify-center min-h-[44px] min-w-[44px]"
                style={{ color: "var(--oh-text-secondary)" }} data-testid="dal-drawer-close" aria-label="Fermer le menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            {renderDalMenu(() => setMenuOpen(false))}
          </div>
        </div>
      )}

      {/* Zone nav — Besoins | Sélection | (zones cochées) | Documents.
          MOBILE uniquement (stepper haut). Desktop (embedded) → menu latéral. */}
      {!embedded && (
      <nav className="px-2 py-2 space-y-1.5" style={{ background: "var(--oh-bg-surface)" }}>
        {/* Niveau 1 : onglets principaux */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          <button onClick={() => setCurrentStepKey("infos")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all shrink-0 font-medium"
            style={isInfosStep ? menuActive : menuIdle}
            data-testid="mobile-dal-step-infos">
            <User className="w-3.5 h-3.5" /> Infos
          </button>
          <button onClick={() => setCurrentStepKey("besoins")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all shrink-0 font-medium"
            style={isBesoinsStep ? menuActive : menuIdle}
            data-testid="mobile-dal-step-besoins">
            <User className="w-3.5 h-3.5" /> Besoins
          </button>
          <button onClick={() => setCurrentStepKey("selection")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all shrink-0 font-medium"
            style={isSelectionStep ? menuActive : menuIdle}
            data-testid="mobile-dal-step-selection">
            <CheckCircle2 className="w-3.5 h-3.5" /> Zones ({selectedZoneIds.length})
          </button>
          <button onClick={() => setCurrentStepKey("solutions")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border whitespace-nowrap transition-all shrink-0 font-medium"
            style={isSolutionsStep ? menuActive : menuIdle}
            data-testid="mobile-dal-step-solutions">
            <Target className="w-3.5 h-3.5" /> Solutions
            {solutions.length > 0 && <Badge className="text-[10px] px-1.5 py-0" style={accentTint}>{solutions.length}</Badge>}
          </button>
          <button onClick={() => setCurrentStepKey("docs")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border whitespace-nowrap transition-all shrink-0 font-medium"
            style={isDocsStep ? menuActive : menuIdle}
            data-testid="mobile-dal-step-documents">
            <FileText className="w-3.5 h-3.5" /> Docs
            {documents.length > 0 && <Badge className="text-[10px] px-1.5 py-0" style={accentTint}>{documents.length}</Badge>}
          </button>
        </div>
        {/* Niveau 2 : zones sélectionnées (raccourcis) */}
        {selectedZoneIds.length > 0 && (
          <div className="flex gap-1 overflow-x-auto no-scrollbar pl-1 items-center">
            {selectedZoneIds.map(zid => {
              const zone = zonesConfig.find(z => z.id === zid);
              if (!zone) return null;
              const ZIcon = zone.icon;
              const isActive = currentStepKey === zone.id;
              const hasData = zonesData[zone.id]?.photos.length > 0 || (zonesData[zone.id]?.travaux || []).length > 0;
              return (
                <button key={zone.id} onClick={() => setCurrentStepKey(zone.id)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border whitespace-nowrap transition-all shrink-0 font-medium"
                  style={isActive ? zoneActive : zoneIdle}
                  data-testid={`mobile-dal-step-${zone.id}`}>
                  <ZIcon className="w-3 h-3" />
                  {zone.name.split('/')[0].trim()}
                  {hasData && <CheckCircle2 className="w-2.5 h-2.5 shrink-0" style={{ color: "var(--oh-success-text)" }} />}
                </button>
              );
            })}
          </div>
        )}
      </nav>
      )}

      {/* Content — desktop (embedded) : menu latéral GAUCHE + panneau DROITE ;
          mobile : flux vertical plein écran (wrapper display:contents = neutre). */}
      <div className={embedded ? "flex-1 flex min-h-0 overflow-hidden" : "contents"}>
        {/* Charte §9 — aside DESKTOP uniquement ; sur mobile le menu passe en drawer (burger). */}
        {embedded && (
          <aside className="hidden md:block w-56 shrink-0 overflow-y-auto border-r p-2" style={{ borderColor: "var(--oh-border)", background: "var(--oh-bg-surface)" }} data-testid="dal-left-menu">
            {renderDalMenu()}
          </aside>
        )}

      {/* Content */}
      <main className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-4 ${embedded ? "pb-4" : "pb-28"}`}>
        {showInfosAll && (
          /* ===== ÉTAPE INFOS (DAL Phase 2) — 3 blocs, source unique bidirectionnelle ===== */
          <DalInfosStep initialData={initialData} patchField={patchField} clientName={clientName} />
        )}
        {showInfosBenef && (
          <DalInfosStep initialData={initialData} patchField={patchField} clientName={clientName} section="beneficiaire" />
        )}
        {showInfosLog && (
          <DalInfosStep initialData={initialData} patchField={patchField} clientName={clientName} section="logement" />
        )}
        {showInfosHdcp && (
          <DalInfosStep initialData={initialData} patchField={patchField} clientName={clientName} section="handicap" />
        )}
        {showSelection && (
          /* ===== ÉCRAN SÉLECTION DES ZONES (P2) ===== */
          <ZoneSelectionScreen
            zonesConfig={zonesConfig}
            zoneGroups={ZONE_GROUPS}
            selectedZoneIds={selectedZoneIds}
            onToggleZone={toggleSelectedZone}
            onStart={handleStartFromSelection}
          />
        )}
        {showBesoins && (
          /* ===== BESOINS STEP ===== */
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><User className="w-5 h-5" /></div>
              <div><h2 className="text-lg font-medium" style={txtMain}>Profil & besoins</h2><p className="text-xs" style={txtSec}>Informations terrain sur le bénéficiaire</p></div>
            </div>

            {/* Point 4 (2026-07-02) : la surface habitable est REGROUPÉE avec les
                autres valeurs Logement (année/pièces/niveaux) dans le bloc
                « Infos Logement » de l'étape Infos — plus de champ isolé ici, plus
                de blocage (surface = alerte non bloquante, cf. seuil complétude). */}

            {/* Info dossier pré-remplie depuis OmniHub (lecture seule) */}
            {(dossierInfo?.reference || dossierInfo?.type_projet || addressStr) && (
              <div className="rounded-lg p-3 border" style={infoStyle} data-testid="mobile-dal-dossier-info">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase tracking-wide">Données dossier (pré-remplies)</span>
                </div>
                <div className="space-y-1 text-xs">
                  {dossierInfo?.reference && (
                    <div className="flex gap-2"><span className="min-w-[80px] opacity-70">Référence</span><span className="font-mono">{dossierInfo.reference}</span></div>
                  )}
                  <div className="flex gap-2"><span className="min-w-[80px] opacity-70">Client</span><span>{clientName}</span></div>
                  {dossierInfo?.type_projet && (
                    <div className="flex gap-2"><span className="min-w-[80px] opacity-70">Projet</span><span>{dossierInfo.type_projet}</span></div>
                  )}
                  {addressStr && (
                    <div className="flex gap-2"><span className="min-w-[80px] opacity-70">Adresse</span><span className="flex-1">{addressStr}</span></div>
                  )}
                  {dossierInfo?.surface_habitable && (
                    <div className="flex gap-2"><span className="min-w-[80px] opacity-70">Surface</span><span>{dossierInfo.surface_habitable} m²</span></div>
                  )}
                </div>
                <p className="text-[10px] mt-2 italic opacity-70">Ces informations viennent du dossier OmniHub. Modifier depuis la fiche dossier si erreur.</p>
              </div>
            )}

            {/* Intervenant selector */}
            <div className="rounded-lg p-3 border" style={surfaceStyle}>
              <Label className="text-xs font-medium uppercase tracking-wide mb-2 block" style={accentText}>Intervenant (visiteur)</Label>
              {visiteurs.length > 0 ? (
                <select
                  value={intervenant.nom ? `${intervenant.nom}|${intervenant.prenom || ""}|${intervenant.fonction || ""}|${intervenant.telephone || ""}` : ""}
                  onChange={e => {
                    if (e.target.value === "__new__") {
                      setIntervenant({ nom: "", prenom: "", fonction: "", telephone: "" });
                    } else if (e.target.value) {
                      // FT5 — auto-remplissage : on récupère AUSSI le téléphone du
                      // visiteur (4ᵉ segment) et on le patch pour éviter la re-saisie.
                      const [nom, prenom, fonction, telephone] = e.target.value.split("|");
                      const next = { nom, prenom: prenom || "", fonction: fonction || "", telephone: telephone || "" };
                      setIntervenant(next);
                      patchField("intervenant.nom", next.nom);
                      patchField("intervenant.prenom", next.prenom);
                      patchField("intervenant.fonction", next.fonction);
                      patchField("intervenant.telephone", next.telephone);
                    }
                  }}
                  className="w-full h-10 px-3 rounded-md border text-sm" style={inputStyle}
                  data-testid="mobile-dal-intervenant-select"
                >
                  <option value="">Sélectionner un visiteur</option>
                  {visiteurs.map((v, i) => (
                    <option key={v.id || `${v.nom}|${v.prenom || ""}|${i}`} value={`${v.nom}|${v.prenom || ""}|${v.fonction || v.type || ""}|${v.telephone || ""}`}>
                      {v.nom} {v.prenom || ""} — {v.fonction || v.type || "AMO"}
                    </option>
                  ))}
                  <option value="__new__">+ Saisir manuellement</option>
                </select>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                  <Input value={intervenant.nom} onChange={e => setIntervenant(p => ({...p, nom: e.target.value}))} onBlur={e => patchField("intervenant.nom", e.target.value)} placeholder="Nom" className="text-sm" style={inputStyle} />
                  <Input value={intervenant.prenom} onChange={e => setIntervenant(p => ({...p, prenom: e.target.value}))} onBlur={e => patchField("intervenant.prenom", e.target.value)} placeholder="Prénom" className="text-sm" style={inputStyle} />
                  <Input value={intervenant.fonction} onChange={e => setIntervenant(p => ({...p, fonction: e.target.value}))} onBlur={e => patchField("intervenant.fonction", e.target.value)} placeholder="Fonction" className="text-sm" style={inputStyle} />
                  <Input value={intervenant.telephone || ""} onChange={e => setIntervenant(p => ({...p, telephone: e.target.value}))} onBlur={e => patchField("intervenant.telephone", e.target.value)} placeholder="Téléphone" className="text-sm" style={inputStyle} data-testid="mobile-dal-intervenant-tel" />
                </div>
              )}
              {/* Manual inputs shown when visiteur selected but need override.
                  FT5 — le téléphone du visiteur y est AUTO-REMPLI (modifiable). */}
              {intervenant.nom && visiteurs.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <Input value={intervenant.nom} onChange={e => setIntervenant(p => ({...p, nom: e.target.value}))} onBlur={e => patchField("intervenant.nom", e.target.value)} placeholder="Nom" className="text-xs h-8" style={inputStyle} />
                  <Input value={intervenant.prenom} onChange={e => setIntervenant(p => ({...p, prenom: e.target.value}))} onBlur={e => patchField("intervenant.prenom", e.target.value)} placeholder="Prénom" className="text-xs h-8" style={inputStyle} />
                  <Input value={intervenant.fonction} onChange={e => setIntervenant(p => ({...p, fonction: e.target.value}))} onBlur={e => patchField("intervenant.fonction", e.target.value)} placeholder="Fonction" className="text-xs h-8" style={inputStyle} />
                  <Input value={intervenant.telephone || ""} onChange={e => setIntervenant(p => ({...p, telephone: e.target.value}))} onBlur={e => patchField("intervenant.telephone", e.target.value)} placeholder="Téléphone" className="text-xs h-8" style={inputStyle} data-testid="mobile-dal-intervenant-tel-override" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs" style={txtSec}>Âge{autoAge != null ? " · auto (date de naissance)" : ""}</Label>
                {autoAge != null ? (
                  <Input type="number" value={besoins.age} readOnly disabled title="Calculé automatiquement depuis la date de naissance du bénéficiaire" placeholder="78" className="opacity-70 cursor-not-allowed" style={inputStyle} data-testid="dal-age-auto" />
                ) : (
                  <Input type="number" value={besoins.age} onChange={e => setBesoins(p => ({...p, age: e.target.value}))} onBlur={e => patchField("besoins.age", e.target.value)} placeholder="78" style={inputStyle} data-testid="dal-age-manual" />
                )}
              </div>
              <div>
                <Label className="text-xs" style={txtSec}>Mobilité</Label>
                <select value={besoins.mobilite} onChange={e => updateBesoin("mobilite", e.target.value)} className="w-full h-10 px-3 rounded-md border text-sm" style={inputStyle}>
                  <option value="autonome">Autonome</option>
                  <option value="aide_technique">Aide technique</option>
                  <option value="aide_humaine">Aide humaine</option>
                  <option value="fauteuil">Fauteuil roulant</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs" style={txtSec}>GIR</Label>
                <select
                  value={besoins.gir || ""}
                  onChange={e => updateBesoin("gir", e.target.value)}
                  disabled={isLockedCanonical("gir")}
                  className={`w-full h-10 px-3 rounded-md border text-sm ${isLockedCanonical("gir") ? "opacity-60 cursor-not-allowed" : ""}`} style={inputStyle}
                >
                  <option value="">Non évalué</option>
                  {[1,2,3,4,5,6].map(n => <option key={n} value={String(n)}>GIR {n}</option>)}
                </select>
                {isLockedCanonical("gir") && (
                  <p className="text-[10px] mt-0.5" style={txtMuted}>{lockedHint}</p>
                )}
              </div>
              <div className="flex flex-col justify-end pb-2 gap-1">
                <div className="flex items-center gap-4">
                  <label
                    className={`flex items-center gap-1.5 text-sm ${isLockedCanonical("apa") ? "opacity-60 cursor-not-allowed" : ""}`} style={txtSec}
                    title={isLockedCanonical("apa") ? lockedHint : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={besoins.apa}
                      onChange={e => updateBesoin("apa", e.target.checked)}
                      disabled={isLockedCanonical("apa")}
                      className="rounded"
                    /> APA
                  </label>
                  <label className="flex items-center gap-1.5 text-sm" style={txtSec}>
                    <input type="checkbox" checked={besoins.rqth} onChange={e => updateBesoin("rqth", e.target.checked)} className="rounded" /> RQTH
                  </label>
                </div>
                {isLockedCanonical("apa") && (
                  <p className="text-[10px]" style={txtMuted}>{lockedHint} (APA)</p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs" style={txtSec}>Historique (chutes, incidents)</Label>
                <div className="flex items-center gap-1">
                  <MicButton
                    value={besoins.historique_chutes}
                    onTranscript={v => { setBesoins(p => ({...p, historique_chutes: v})); patchField("besoins.historique_chutes", v); }}
                  />
                  <AITranscribeBtn value={besoins.historique_chutes} onResult={v => { setBesoins(p => ({...p, historique_chutes: v})); patchField("besoins.historique_chutes", v); }} context="historique de chutes du bénéficiaire MaPrimeAdapt" dossierId={dossierId} />
                </div>
              </div>
              <Textarea value={besoins.historique_chutes} onChange={e => setBesoins(p => ({...p, historique_chutes: e.target.value}))} onBlur={e => patchField("besoins.historique_chutes", e.target.value)} placeholder="Ex: 2 chutes en 6 mois, glissade sdb..."
                className="min-h-[80px]" style={inputStyle} />
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs font-medium" style={{ color: "var(--oh-error-text)" }}>Difficultés quotidiennes *</Label>
                <div className="flex items-center gap-1">
                  <MicButton
                    value={besoins.difficultes_quotidiennes}
                    onTranscript={v => { setBesoins(p => ({...p, difficultes_quotidiennes: v})); patchField("besoins.difficultes_quotidiennes", v); }}
                  />
                  <AITranscribeBtn value={besoins.difficultes_quotidiennes} onResult={v => { setBesoins(p => ({...p, difficultes_quotidiennes: v})); patchField("besoins.difficultes_quotidiennes", v); }} context="difficultés quotidiennes du bénéficiaire âgé MaPrimeAdapt ANAH" dossierId={dossierId} />
                </div>
              </div>
              <Textarea value={besoins.difficultes_quotidiennes} onChange={e => setBesoins(p => ({...p, difficultes_quotidiennes: e.target.value}))} onBlur={e => patchField("besoins.difficultes_quotidiennes", e.target.value)}
                placeholder="Ex: ne peut plus enjamber la baignoire, se tient aux meubles..."
                className="min-h-[100px]" style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)", borderColor: "var(--oh-error-border)" }} />
              <p className="text-xs mt-1" style={{ color: "var(--oh-error-text)" }}>Champ clé pour la solidité du dossier ANAH</p>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs font-medium" style={txtSec}>Usage réel du logement</Label>
                <div className="flex items-center gap-1">
                  <MicButton value={besoins.usage_reel} onTranscript={v => { setBesoins(p => ({...p, usage_reel: v})); patchField("besoins.usage_reel", v); }} />
                  <AITranscribeBtn value={besoins.usage_reel} onResult={v => { setBesoins(p => ({...p, usage_reel: v})); patchField("besoins.usage_reel", v); }} context="usage réel du logement par le bénéficiaire (diagnostic autonomie MaPrimeAdapt')" dossierId={dossierId} label="Améliorer IA" testId="dal-usage-reel-ia" />
                </div>
              </div>
              <Textarea value={besoins.usage_reel} onChange={e => setBesoins(p => ({...p, usage_reel: e.target.value}))} onBlur={e => patchField("besoins.usage_reel", e.target.value)}
                placeholder="Ex: vit seul, ne monte plus à l'étage, dort au rez-de-chaussée..."
                className="min-h-[80px]" style={inputStyle} data-testid="dal-usage-reel" />
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs font-medium" style={txtSec}>Observations générales</Label>
                <div className="flex items-center gap-1">
                  <MicButton value={besoins.observations} onTranscript={v => { setBesoins(p => ({...p, observations: v})); patchField("observations", v); }} />
                  <AITranscribeBtn value={besoins.observations} onResult={v => { setBesoins(p => ({...p, observations: v})); patchField("observations", v); }} context="observations générales du diagnostic autonomie logement (notes intervenant/visiteur MaPrimeAdapt')" dossierId={dossierId} label="Améliorer IA" testId="dal-observations-ia" />
                </div>
              </div>
              <Textarea value={besoins.observations} onChange={e => setBesoins(p => ({...p, observations: e.target.value}))} onBlur={e => patchField("observations", e.target.value)}
                placeholder="Notes libres de l'intervenant / visiteur..."
                className="min-h-[80px]" style={inputStyle} data-testid="dal-observations" />
            </div>

            <JournalVisite dossierId={dossierId} />
          </div>
        )}
        {emb && activeSection === "zones" && selectedZoneIds.length > 0 && (
          <div className="flex gap-1 flex-wrap items-center mb-4" data-testid="dal-zones-chips">
            {selectedZoneIds.map(zid => {
              const zone = zonesConfig.find(z => z.id === zid);
              if (!zone) return null;
              const ZIcon = zone.icon;
              const isActive = currentStepKey === zone.id;
              const hasData = zonesData[zone.id]?.photos.length > 0 || (zonesData[zone.id]?.travaux || []).length > 0;
              return (
                <button key={zone.id} onClick={() => setCurrentStepKey(zone.id)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border whitespace-nowrap transition-all shrink-0 font-medium"
                  style={isActive ? zoneActive : zoneIdle}
                  data-testid={`dal-zone-chip-${zone.id}`}>
                  <ZIcon className="w-3 h-3" />
                  {zone.name.split('/')[0].trim()}
                  {hasData && <CheckCircle2 className="w-2.5 h-2.5 shrink-0" style={{ color: "var(--oh-success-text)" }} />}
                </button>
              );
            })}
          </div>
        )}
        {emb && activeSection === "zones" && !currentZone && (
          <div className="text-center py-8 text-sm" style={txtSec} data-testid="dal-zones-pick-hint">
            {selectedZoneIds.length > 0 ? "Sélectionnez une zone ci-dessus pour l'évaluer." : "Cochez d'abord les zones concernées dans la liste ci-dessus."}
          </div>
        )}
        {showZoneEval && (
          /* ===== ZONE STEP ===== */
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}>
                <currentZone.icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-medium" style={txtMain}>{currentZone.name}</h2>
                <p className="text-xs" style={txtSec}>{currentZone.critical ? "Zone critique" : `Zone ${selectedZoneIds.indexOf(currentZone.id) + 1}/${selectedZoneIds.length}`}</p>
              </div>
              <button onClick={() => toggleZone(currentZone.id)} className="p-2 rounded-lg border" style={zonesData[currentZone.id].enabled ? successStyle : { background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)", borderColor: "var(--oh-border)" }}>
                {zonesData[currentZone.id].enabled ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
              </button>
            </div>

            {zonesData[currentZone.id].enabled ? (
              <>
                {/* 1. Informations */}
                <section>
                  <Label className="text-sm mb-2 block font-medium" style={txtSec}>Informations</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentZone.fields.map(field => (
                      <div key={field.id} className={field.type === "textarea" ? "col-span-2" : ""}>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs" style={txtSec}>{field.label}{field.required && <span style={{ color: "var(--oh-error-text)" }}>*</span>}</Label>
                          {field.type === "textarea" && (
                            <div className="flex items-center gap-1">
                              <MicButton
                                value={zonesData[currentZone.id].fields[field.id]}
                                onTranscript={v => { updateField(currentZone.id, field.id, v); blurField(currentZone.id, field.id, v); }}
                              />
                              <AITranscribeBtn value={zonesData[currentZone.id].fields[field.id]} onResult={v => { updateField(currentZone.id, field.id, v); blurField(currentZone.id, field.id, v); }} context={`observations zone ${currentZone.name} diagnostic autonomie logement`} dossierId={dossierId} />
                            </div>
                          )}
                        </div>
                        {field.type === "textarea" ? (
                          <Textarea value={zonesData[currentZone.id].fields[field.id] || ""} onChange={e => updateField(currentZone.id, field.id, e.target.value)} onBlur={e => blurField(currentZone.id, field.id, e.target.value)} placeholder={field.placeholder} className="min-h-[80px]" style={inputStyle} />
                        ) : field.type === "select" ? (
                          <select value={zonesData[currentZone.id].fields[field.id] || ""} onChange={e => { updateField(currentZone.id, field.id, e.target.value); blurField(currentZone.id, field.id, e.target.value); }} className="w-full h-10 px-3 rounded-md border text-sm" style={inputStyle}>
                            <option value="">Sélectionner</option>
                            {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <Input type={field.type} value={zonesData[currentZone.id].fields[field.id] || ""} onChange={e => updateField(currentZone.id, field.id, e.target.value)} onBlur={e => blurField(currentZone.id, field.id, e.target.value)} placeholder={field.placeholder} style={inputStyle} />
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* 2. Travaux préconisés */}
                <section>
                  <Label className="text-sm mb-2 block font-medium" style={txtSec}>Travaux préconisés</Label>
                  <div className="space-y-1.5">
                    {currentZone.travaux.map(t => {
                      const sel = zonesData[currentZone.id].travaux.includes(t.id);
                      return (
                        <button key={t.id} onClick={() => toggleTravail(currentZone.id, t.id)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg text-left border"
                          style={sel ? zoneActive : { background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}
                          data-testid={`travail-${currentZone.id}-${t.id}`}>
                          {sel ? <CheckCircle2 className="w-5 h-5 shrink-0" style={accentText} /> : <Circle className="w-5 h-5 shrink-0" style={txtMuted} />}
                          <span className="text-sm">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* 2b. Préconisations reprises du DAL desktop (CC-AUDIT-43 L0) — back-projetées
                    mais non mappables en case canonique : affichées pour que l'AMO les transforme
                    en travaux cochés ci-dessus. Anti-perte de la migration de shape. */}
                {Array.isArray(zonesData[currentZone.id].fields?.preconisations_legacy)
                  && zonesData[currentZone.id].fields.preconisations_legacy.length > 0 && (
                  <section data-testid={`legacy-precos-${currentZone.id}`}>
                    <Label className="text-sm mb-2 block font-medium" style={{ color: "var(--oh-info-text)" }}>
                      Préconisations reprises du DAL précédent — à cocher ci-dessus
                    </Label>
                    <ul className="space-y-1 rounded-lg border p-3" style={infoStyle}>
                      {zonesData[currentZone.id].fields.preconisations_legacy.map((p, i) => (
                        <li key={i} className="text-xs">• {p}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* 3. Plan 2D IA */}
                <MobilePlan2DAI
                  zoneName={currentZone.name}
                  zoneId={currentZone.id}
                  dossierId={dossierId}
                  dimensions={{
                    width: zonesData[currentZone.id].fields?.largeur || "",
                    height: zonesData[currentZone.id].fields?.longueur || ""
                  }}
                />

                {/* 3a. Éditeur de plan AVANCÉ (Konva) — OPTIONNEL & SECONDAIRE.
                    Hors flux Précédent/Suivant : panneau repliable, fermé par
                    défaut. Réutilise Plan2DGenerator (même éditeur que le DAL
                    desktop) pour pièces en L/T + équipements draggables.
                    Disponible partout (terrain tablette possible) mais clairement
                    secondaire ; un hint indique le confort optimal sur écran large
                    quand on n'est pas en mode desktop (embedded). */}
                <section data-testid={`plan-2d-advanced-${currentZone.id}`}>
                  <button
                    type="button"
                    onClick={() => setShowKonvaEditor(v => !v)}
                    className="w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-accent-text)" }}
                    data-testid={`toggle-plan-2d-advanced-${currentZone.id}`}
                    aria-expanded={showKonvaEditor}
                  >
                    <span className="flex items-center gap-2">
                      <Shapes className="w-4 h-4" /> Croquis des travaux — plan 2D (pièces en L, équipements)
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${showKonvaEditor ? "rotate-90" : ""}`} />
                  </button>
                  <p className="mt-1 text-[11px] leading-tight" style={txtSec}>
                    Dessinez le croquis directement ici (doigt sur mobile). Plus confortable sur grand écran, mais pleinement utilisable sur mobile.
                  </p>
                  {showKonvaEditor && (
                    <div className="mt-2">
                      <Plan2DGenerator
                        key={`konva-${currentZone.id}`}
                        dossierId={dossierId}
                        tenantId={tenantId}
                        roomData={{
                          // Mirror DALForm desktop : width = largeur, height = longueur
                          width: parseFloat(zonesData[currentZone.id].fields?.largeur) || undefined,
                          height: parseFloat(zonesData[currentZone.id].fields?.longueur) || undefined,
                          name: currentZone.name,
                        }}
                        onSave={savePlan2D}
                      />
                      {plan2dEditorMsg && (
                        <p
                          className="mt-2 text-xs" style={{ color: plan2dEditorMsg.type === "error" ? "var(--oh-error-text)" : plan2dEditorMsg.type === "saving" ? "var(--oh-text-secondary)" : "var(--oh-success-text)" }}
                          data-testid={`plan-2d-advanced-msg-${currentZone.id}`}
                        >
                          {plan2dEditorMsg.text}
                        </p>
                      )}
                    </div>
                  )}
                </section>

                {/* 3b. Vue 3D IA Après Travaux */}
                {zonesData[currentZone.id].travaux.length > 0 && (
                  <section>
                    <Label className="text-sm flex items-center gap-2 mb-2 font-medium" style={txtSec}>
                      <Sparkles className="w-4 h-4" style={accentText} /> Vue 3D après travaux
                    </Label>
                    {zone3dImages[currentZone.id] ? (
                      <div className="space-y-2">
                        <img
                          src={zone3dImages[currentZone.id].image_base64
                            ? `data:image/png;base64,${zone3dImages[currentZone.id].image_base64}`
                            : `${API}${zone3dImages[currentZone.id].url}`}
                          alt="Vue 3D après travaux"
                          className="w-full rounded-xl border max-h-48 object-cover" style={{ borderColor: "var(--oh-border)" }}
                        />
                        <button
                          onClick={() => generateZone3D(currentZone)}
                          disabled={generatingZone3d[currentZone.id]}
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs border" style={btnSecondary}
                          data-testid={`mobile-regen-3d-${currentZone.id}`}
                        >
                          {generatingZone3d[currentZone.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          Régénérer
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => generateZone3D(currentZone)}
                        disabled={generatingZone3d[currentZone.id]}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed text-sm" style={btnSecondary}
                        data-testid={`mobile-gen-3d-${currentZone.id}`}
                      >
                        {generatingZone3d[currentZone.id] ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Génération en cours…</>
                        ) : (
                          <><Sparkles className="w-4 h-4" /> Générer vue 3D IA</>
                        )}
                      </button>
                    )}
                  </section>
                )}

                {/* 4. Photos (en dernier) */}
                <section>
                  <Label className="text-sm flex items-center gap-2 mb-2 font-medium" style={txtSec}><Camera className="w-4 h-4" /> Photos ({zonesData[currentZone.id].photos.length})</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {zonesData[currentZone.id].photos.map(photo => (
                      <div key={photo.id} className="relative aspect-square group">
                        <img src={photo.localUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                        <button onClick={() => deletePhoto(currentZone.id, photo.id)} className="absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "var(--oh-error-border)", color: "var(--oh-accent-contrast)" }}><X className="w-3 h-3" /></button>
                        {/* Badge statut upload (fix P1 2026-05-20) */}
                        {photo.uploaded && (
                          <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[10px] font-medium rounded border" style={successStyle} data-testid={`photo-uploaded-${photo.id}`}>✓ Envoyée</span>
                        )}
                        {photo.uploading && (
                          <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[10px] font-medium rounded border inline-flex items-center gap-1" style={infoStyle} data-testid={`photo-uploading-${photo.id}`}>
                            <Loader2 className="w-2.5 h-2.5 animate-spin" /> Envoi…
                          </span>
                        )}
                        {photo.uploadError && (
                          <span className="absolute bottom-1 left-1 right-1 px-1.5 py-0.5 text-[10px] font-medium rounded border truncate" style={errorStyle} data-testid={`photo-error-${photo.id}`} title={photo.uploadError}>
                            ✗ {photo.uploadError.length > 18 ? photo.uploadError.slice(0, 18) + "…" : photo.uploadError}
                          </span>
                        )}
                      </div>
                    ))}
                    <button onClick={() => cameraInputRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1" style={{ borderColor: "var(--oh-accent)" }}>
                      <Camera className="w-5 h-5" style={accentText} /><span className="text-[10px]" style={txtSec}>Caméra</span>
                    </button>
                    <button onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1" style={{ borderColor: "var(--oh-border)" }}>
                      <Upload className="w-5 h-5" style={txtMuted} /><span className="text-[10px]" style={txtSec}>Galerie</span>
                    </button>
                  </div>
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoCapture} />
                  <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoCapture} />
                </section>
              </>
            ) : (
              <div className="text-center py-12"><X className="w-12 h-12 mx-auto mb-3" style={txtMuted} /><p style={txtSec}>Zone non concernée</p></div>
            )}
          </div>
        )}
        {showSolutions && (
          /* ===== SOLUTIONS STEP (mapping risques ANAH ↔ solutions AMO) ===== */
          <div className="space-y-4" data-testid="mobile-dal-solutions-panel">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><Target className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-medium" style={txtMain}>Solutions proposées</h2>
                <p className="text-xs" style={txtSec}>Saisissez un mot-clé → l'IA rédige la préconisation argumentée</p>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={regenerateSolutions}
                className="h-8 px-2 text-xs shrink-0" style={btnSecondary}
                data-testid="mobile-dal-solutions-regenerate"
                title="Reconstruit le mapping depuis les travaux cochés par zone"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1" /> Régénérer
              </Button>
            </div>

            <p className="text-[11px] leading-tight" style={txtSec}>
              Amorce dérivée des travaux cochés par zone (squelette). Pour chaque ligne,
              saisissez un mot-clé court (ex. « motorisation volet ») et cliquez
              <span className="font-medium" style={accentText}> Rédiger IA </span>
              → l'IA produit une préconisation rédigée et argumentée (ANAH/MaPrimeAdapt'),
              que vous pouvez ensuite éditer. Ces solutions alimentent la section
              « Solutions proposées » du PDF DAL.
            </p>

            {solutions.length === 0 ? (
              <div className="text-center py-8 rounded-lg border border-dashed" style={surfaceStyle} data-testid="mobile-dal-solutions-empty">
                <Target className="w-10 h-10 mx-auto mb-2" style={txtMuted} />
                <p className="text-sm" style={txtSec}>Aucune solution pour l'instant</p>
                <p className="text-xs mt-1 mb-3" style={txtMuted}>Cochez des travaux dans les zones, puis générez le mapping.</p>
                <Button
                  size="sm" variant="outline"
                  onClick={regenerateSolutions}
                  className="text-xs" style={btnSecondary}
                  data-testid="mobile-dal-solutions-generate"
                >
                  <Wand2 className="w-3.5 h-3.5 mr-1" /> Générer depuis les zones
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {solutions.map((s, idx) => {
                  const zoneName = zonesConfig.find(z => z.id === s.zone)?.name;
                  return (
                    <div key={idx} className="rounded-lg p-2.5 space-y-2 border" style={surfaceStyle} data-testid={`mobile-dal-solution-${idx}`}>
                      <div className="flex items-center justify-between gap-2">
                        {zoneName ? (
                          <Badge className="text-[10px]" style={accentTint}>{zoneName}</Badge>
                        ) : <span className="text-[10px]" style={txtSec}>Zone non liée</span>}
                        <button
                          onClick={() => deleteSolution(idx)}
                          className="p-1 rounded shrink-0"
                          aria-label="Supprimer cette solution"
                          data-testid={`mobile-dal-solution-delete-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" style={{ color: "var(--oh-error-text)" }} />
                        </button>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide" style={txtSec}>Problème identifié</Label>
                        <Input
                          value={s.probleme || ""}
                          onChange={e => updateSolution(idx, "probleme", e.target.value)}
                          placeholder="Ex: ne peut plus enjamber la baignoire"
                          className="text-sm h-9" style={inputStyle}
                          data-testid={`mobile-dal-solution-probleme-${idx}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 shrink-0" style={accentText} />
                        <div className="flex-1">
                          <Label className="text-[10px] uppercase tracking-wide" style={txtSec}>Solution préconisée</Label>
                          <Input
                            value={s.solution || ""}
                            onChange={e => updateSolution(idx, "solution", e.target.value)}
                            placeholder="Mot-clé court (ex. « motorisation volet ») puis Rédiger IA"
                            className="text-sm h-9" style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)", borderColor: "var(--oh-accent)" }}
                            data-testid={`mobile-dal-solution-solution-${idx}`}
                          />
                          {/* Rédiger IA — mot-clé court → préconisation argumentée
                              (réutilise /dal/transcribe, rédacteur ANAH/MaPrimeAdapt').
                              Apparaît dès qu'un texte est saisi (cf. AITranscribeBtn). */}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px]" style={txtSec}>Dictez ou tapez un mot-clé → IA rédige</span>
                            <div className="flex items-center gap-1">
                            <MicButton value={s.solution} onTranscript={(v) => updateSolution(idx, "solution", v)} />
                            <AITranscribeBtn
                              value={s.solution}
                              onResult={(txt) => updateSolution(idx, "solution", txt)}
                              context={`Préconisation de travaux d'adaptation du logement (MaPrimeAdapt'/ANAH).${zoneName ? ` Zone concernée : ${zoneName}.` : ""}${s.probleme ? ` Problème identifié : ${s.probleme}.` : ""} À partir du mot-clé fourni, rédige UNE préconisation de travaux claire, professionnelle et argumentée (utilité pour l'autonomie / la sécurité du bénéficiaire), sans inventer de dimensions chiffrées absentes.`}
                              dossierId={dossierId}
                              label="Rédiger IA"
                              testId={`mobile-dal-solution-rediger-${idx}`}
                            />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button
                  size="sm" variant="outline"
                  onClick={addSolution}
                  className="w-full text-xs" style={btnDiscreet}
                  data-testid="mobile-dal-solutions-add"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une solution
                </Button>
              </div>
            )}
          </div>
        )}
        {showDocs && (
          /* ===== DOCUMENTS STEP ===== */
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><FileText className="w-5 h-5" /></div>
              <div><h2 className="text-lg font-medium" style={txtMain}>Documents administratifs</h2><p className="text-xs" style={txtSec}>Scannez les pièces justificatives du client</p></div>
            </div>

            {/* AG-02 — Audit IA du DAL (verdict + complétude + incohérences + plans).
                Réutilise DALAgentPanel tel quel (O1). Lecture seule, non bloquant.
                MOBILE uniquement — desktop : déplacé dans le groupe IA du menu latéral. */}
            {docsShowAudit && (
              <DALAgentPanel reference={dossierInfo?.reference} tenantId={tenantId} />
            )}

            {/* T2 — Import d'un DAL PDF existant (repart d'un DAL déjà rempli). */}
            <ImportDALPDFBlockMobile
              dossierId={dossierId}
              tenantId={tenantId}
              dalImportedPdf={dalImportedPdf}
              onImported={setDalImportedPdf}
            />

            {/* T5 — Récap de complétude (présence simple, NON bloquant, pas d'IA).
                Informe le visiteur de ce qui manque avant de générer le DAL. */}
            {(() => {
              // VERROU (TASK 2) : compter UNIQUEMENT les entrées réellement
              // renseignées (zones cochées + travaux, solutions non vides) — pas
              // les zones fantômes/canoniques. Valeurs mémoïsées partagées.
              const zonesTravaux = genuineZonesTravaux;
              const solOk = genuineSolutions;
              const ageNum = Number(besoins?.age);
              const checks = [
                { label: "Surface habitable", ok: !!String(surfaceHabitable ?? "").trim() },
                { label: "Âge du bénéficiaire", ok: !!String(besoins?.age ?? "").trim() },
                { label: "Mobilité", ok: !!(besoins?.mobilite && besoins.mobilite !== "") },
                { label: "GIR (si < 70 ans)", ok: !(ageNum < 70) || !!String(besoins?.gir ?? "").trim() },
                { label: "Difficultés quotidiennes", ok: !!(besoins?.difficultes_quotidiennes || "").trim() },
                { label: "≥ 1 zone avec travaux", ok: zonesTravaux > 0, blocking: true },
                { label: "≥ 1 solution préconisée", ok: solOk > 0, blocking: true },
                { label: "Intervenant (nom)", ok: !!(intervenant?.nom || "").trim(), blocking: true },
              ];
              // Seuil complétude (JJ 2026-07-02) : 3 éléments BLOQUANTS (zone travaux,
              // solution, intervenant). Les autres = alertes non bloquantes.
              const bloquants = checks.filter(c => c.blocking && !c.ok);
              const softManque = checks.filter(c => !c.blocking && !c.ok);
              const stateColor = bloquants.length ? "red" : (softManque.length ? "amber" : "green");
              const pair = stateOf;
              return (
                <>
                  <div className="p-3 rounded-lg border" style={pair(stateColor)} data-testid="dal-completeness-recap">
                    <div className="flex items-center gap-2 mb-1">
                      {stateColor === "green"
                        ? <CheckCircle2 className="w-4 h-4" />
                        : <AlertCircle className="w-4 h-4" />}
                      <span className="text-sm font-medium">
                        {bloquants.length
                          ? `Génération bloquée : ${bloquants.length} élément(s) obligatoire(s) manquant(s)`
                          : (softManque.length
                              ? `Complétude : ${softManque.length} alerte(s) non bloquante(s)`
                              : "Complétude : dossier complet")}
                      </span>
                    </div>
                    <p className="text-[11px] mb-1 opacity-80">
                      Obligatoires (bloquent la génération) : zone avec travaux, solution, intervenant. Les autres = alertes non bloquantes (le PDF affiche « Non renseigné »).
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-testid="dal-completeness-items">
                      {checks.map(c => (
                        <span key={c.label} className="text-[11px] px-2 py-0.5 rounded-full border"
                          style={pair(c.ok ? "green" : (c.blocking ? "red" : "amber"))}>
                          {c.ok ? "✓" : "✗"} {c.label}{c.blocking ? " *" : ""}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Point 3 — VERROU : message + bouton Générer désactivé si un des 3 bloquants manque.
                      MOBILE uniquement — desktop : bouton Générer déplacé dans le groupe ACTIONS. */}
                  {docsShowGenerate && (
                    <>
                      {bloquants.length > 0 && (
                        <div className="rounded-lg border p-2.5 text-xs mt-2" style={pair("red")} data-testid="dal-generate-blocked">
                          Génération bloquée — complétez d'abord : {bloquants.map(b => b.label).join(", ")}.
                        </div>
                      )}
                      <Button
                        onClick={handleGenerate}
                        disabled={saving || pdfGenerating || bloquants.length > 0}
                        className="w-full disabled:opacity-50 mt-2" style={btnPrimary}
                        data-testid="mobile-dal-generate-btn"
                      >
                        {pdfGenerating
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération du DAL…</>
                          : <><FileText className="w-4 h-4 mr-2" /> Générer le DAL</>}
                      </Button>
                    </>
                  )}
                </>
              );
            })()}

            {/* Checklist pièces ANAH — présentes / manquantes */}
            {docsChecklistLoading && (
              <div className="rounded-lg p-3 border flex items-center gap-2 text-xs" style={{ ...surfaceStyle, color: "var(--oh-text-secondary)" }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la check-list ANAH...
              </div>
            )}
            {docsChecklistError && (
              <div className="rounded-lg p-3 border text-xs flex items-start gap-2" style={errorStyle} data-testid="mobile-dal-checklist-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{docsChecklistError} — <button onClick={loadDocsChecklist} className="underline">Réessayer</button></span>
              </div>
            )}
            {docsChecklist && !docsChecklistLoading && (
              <>
                {/* Présents — succès */}
                {docsChecklist.presents?.length > 0 && (
                  <section className="rounded-lg p-3 border" style={successStyle} data-testid="mobile-dal-docs-presents">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-medium uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--oh-success-text)" }}>
                        <CheckCircle2 className="w-4 h-4" /> Pièces présentes ({docsChecklist.presents.length})
                      </Label>
                      <span className="text-xs opacity-80">{docsChecklist.required_present}/{docsChecklist.required_total} obligatoires</span>
                    </div>
                    <ul className="space-y-1">
                      {docsChecklist.presents.map(p => (
                        <li key={p.id} className="flex items-center gap-2 text-xs">
                          <Check className="w-3 h-3 flex-shrink-0" />
                          <span className="font-medium">{p.nom}</span>
                          {p.source_document && <span className="truncate opacity-70">— {p.source_document}</span>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Manquants obligatoires — alerte / info */}
                {docsChecklist.manquants?.length > 0 && (
                  <section className="rounded-lg p-3 border" style={infoStyle} data-testid="mobile-dal-docs-manquants">
                    <Label className="text-xs font-medium uppercase tracking-wide mb-2 flex items-center gap-1" style={{ color: "var(--oh-info-text)" }}>
                      <AlertCircle className="w-4 h-4" /> À collecter ({docsChecklist.manquants.length})
                    </Label>
                    <ul className="space-y-2">
                      {docsChecklist.manquants.map(m => (
                        <li key={m.id} className="flex items-center justify-between gap-2 rounded-md px-2 py-2" style={{ background: "var(--oh-bg-surface)" }}>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate" style={txtMain}>{m.nom}</p>
                            {m.description && <p className="text-[11px] truncate" style={txtSec}>{m.description}</p>}
                          </div>
                          {m.type_document_suggere && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => triggerDirectUploadFor(m.type_document_suggere)}
                              disabled={docDirectUploading === m.type_document_suggere}
                              className="h-8 px-3 text-xs flex-shrink-0" style={btnSecondary}
                              data-testid={`mobile-dal-doc-upload-${m.id}`}
                            >
                              {docDirectUploading === m.type_document_suggere ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Upload...</>
                              ) : (
                                <><Upload className="w-3 h-3 mr-1" /> Uploader</>
                              )}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Manquants optionnels — neutre */}
                {docsChecklist.manquants_optionnels?.length > 0 && (
                  <section className="rounded-lg p-3 border" style={surfaceStyle} data-testid="mobile-dal-docs-optionnels">
                    <Label className="text-xs font-medium uppercase tracking-wide mb-2 block" style={txtSec}>Pièces optionnelles ({docsChecklist.manquants_optionnels.length})</Label>
                    <ul className="space-y-1">
                      {docsChecklist.manquants_optionnels.map(m => (
                        <li key={m.id} className="flex items-center justify-between gap-2 text-xs" style={txtSec}>
                          <span className="truncate">{m.nom}{m.conditions ? ` — ${m.conditions}` : ""}</span>
                          {m.type_document_suggere && (
                            <button
                              onClick={() => triggerDirectUploadFor(m.type_document_suggere)}
                              disabled={docDirectUploading === m.type_document_suggere}
                              className="hover:underline flex-shrink-0 text-xs" style={accentText}
                              data-testid={`mobile-dal-doc-upload-opt-${m.id}`}
                            >
                              {docDirectUploading === m.type_document_suggere ? "..." : "Uploader"}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}

            {/* Input partagé pour les uploads directs depuis les boutons "Uploader" ci-dessus */}
            <input
              ref={docDirectFileRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={handleDirectUpload}
              data-testid="mobile-dal-doc-direct-upload-input"
            />

            {/* Sélection du type + boutons scan */}
            <div className="rounded-lg p-3 border" style={surfaceStyle}>
              <Label className="text-xs font-medium uppercase tracking-wide mb-2 block" style={accentText}>Type de document</Label>
              <select value={selectedDocType} onChange={e => setSelectedDocType(e.target.value)}
                className="w-full h-10 px-3 rounded-md border text-sm mb-3" style={inputStyle}
                data-testid="mobile-dal-doc-type-select">
                {MOBILE_DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button onClick={() => docCameraRef.current?.click()} variant="outline" className="h-16 flex-col gap-1" style={btnSecondary} data-testid="mobile-dal-doc-scan-btn">
                  <ScanLine className="w-6 h-6" />
                  <span className="text-xs">Scanner</span>
                </Button>
                <Button variant="outline" onClick={() => docFileRef.current?.click()} className="h-16 flex-col gap-1" style={btnDiscreet} data-testid="mobile-dal-doc-file-btn">
                  <Upload className="w-6 h-6" />
                  <span className="text-xs">Fichier</span>
                </Button>
              </div>
              <input ref={docCameraRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={handleDocCapture} />
              <input ref={docFileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleDocCapture} />
            </div>

            {/* Liste des documents scannés */}
            {documents.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={txtSec}>{documents.length} document(s) scanné(s)</Label>
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 border rounded-lg" style={cardStyle} data-testid={`mobile-dal-doc-${doc.id}`}>
                    {doc.file?.type?.startsWith("image") ? (
                      <img src={doc.localUrl} alt="" className="w-12 h-12 object-cover rounded-md shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-md flex items-center justify-center shrink-0" style={accentTint}><FileText className="w-6 h-6" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={txtMain}>{doc.typeLabel}</p>
                      <p className="text-xs truncate" style={txtSec}>{doc.fileName}</p>
                    </div>
                    <button onClick={() => deleteDocument(doc.id)} className="p-2 rounded-lg shrink-0" data-testid={`mobile-dal-doc-delete-${doc.id}`}>
                      <Trash2 className="w-4 h-4" style={{ color: "var(--oh-error-text)" }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {documents.length === 0 && (
              <div className="text-center py-8 rounded-lg border border-dashed" style={surfaceStyle}>
                <ScanLine className="w-10 h-10 mx-auto mb-2" style={txtMuted} />
                <p className="text-sm" style={txtSec}>Aucun document scanné</p>
                <p className="text-xs mt-1" style={txtMuted}>Utilisez le bouton Scanner ci-dessus</p>
              </div>
            )}
          </div>
        )}

        {/* ===== PLAN 2D (section dédiée — menu latéral desktop) ===== */}
        {showPlan2d && (
          <div className="space-y-5" data-testid="dal-plan2d-section">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><MapIcon className="w-5 h-5" /></div>
              <div><h2 className="text-lg font-medium" style={txtMain}>Plan 2D</h2><p className="text-xs" style={txtSec}>Générez / importez un plan par zone concernée</p></div>
            </div>
            {planZones.length === 0 ? (
              <div className="text-center py-8 rounded-lg border border-dashed text-sm" style={{ ...surfaceStyle, color: "var(--oh-text-secondary)" }}>
                Aucune zone activée. Cochez des zones dans la section « Zones ».
              </div>
            ) : planZones.map(zone => (
              <div key={zone.id} className="rounded-xl border p-3 space-y-3" style={cardStyle} data-testid={`dal-plan2d-zone-${zone.id}`}>
                <div className="flex items-center gap-2 text-sm font-medium" style={txtMain}>
                  <zone.icon className="w-4 h-4" style={accentText} /> {zone.name}
                </div>
                <MobilePlan2DAI
                  zoneName={zone.name}
                  zoneId={zone.id}
                  dossierId={dossierId}
                  dimensions={{ width: zonesData[zone.id]?.fields?.largeur || "", height: zonesData[zone.id]?.fields?.longueur || "" }}
                />
                <div>
                  <button type="button" onClick={() => setPlan2dOpen(p => ({ ...p, [zone.id]: !p[zone.id] }))}
                    className="w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-accent-text)" }}
                    data-testid={`dal-plan2d-advanced-toggle-${zone.id}`} aria-expanded={!!plan2dOpen[zone.id]}>
                    <span className="flex items-center gap-2"><Shapes className="w-4 h-4" /> Éditeur avancé (pièces en L, équipements)</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${plan2dOpen[zone.id] ? "rotate-90" : ""}`} />
                  </button>
                  {plan2dOpen[zone.id] && (
                    <div className="mt-2">
                      <Plan2DGenerator
                        key={`konva-plan2d-${zone.id}`}
                        dossierId={dossierId}
                        tenantId={tenantId}
                        roomData={{
                          width: parseFloat(zonesData[zone.id]?.fields?.largeur) || undefined,
                          height: parseFloat(zonesData[zone.id]?.fields?.longueur) || undefined,
                          name: zone.name,
                        }}
                        onSave={savePlan2D}
                      />
                      {plan2dEditorMsg && (
                        <p className="mt-2 text-xs" style={{ color: plan2dEditorMsg.type === "error" ? "var(--oh-error-text)" : plan2dEditorMsg.type === "saving" ? "var(--oh-text-secondary)" : "var(--oh-success-text)" }}>
                          {plan2dEditorMsg.text}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== IA — Auditer (menu latéral desktop) ===== */}
        {showIaAudit && (
          <div className="space-y-4" data-testid="dal-ia-audit">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><Sparkles className="w-5 h-5" /></div>
              <div><h2 className="text-lg font-medium" style={txtMain}>Auditer le DAL</h2><p className="text-xs" style={txtSec}>Verdict IA : complétude, incohérences, préconisations</p></div>
            </div>
            <DALAgentPanel reference={dossierInfo?.reference} tenantId={tenantId} />
          </div>
        )}

        {/* ===== IA — Pré-remplir depuis un PDF (menu latéral desktop) ===== */}
        {showIaImport && (
          <div className="space-y-4" data-testid="dal-ia-import">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><ScanLine className="w-5 h-5" /></div>
              <div><h2 className="text-lg font-medium" style={txtMain}>Import DAL</h2><p className="text-xs" style={txtSec}>Importez un DAL existant → extraction &amp; pré-remplissage des champs vides (≠ « Lier DAL » qui rattache le fichier sans l'exploiter)</p></div>
            </div>
            <ImportDALPDFBlockMobile dossierId={dossierId} tenantId={tenantId} dalImportedPdf={dalImportedPdf} onImported={setDalImportedPdf} />
          </div>
        )}

        {/* ===== ACTIONS (menu latéral desktop) — Sauver / Générer / Télécharger ===== */}
        {showActions && (
          <div className="space-y-4" data-testid="dal-actions-section">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><Save className="w-5 h-5" /></div>
              <div><h2 className="text-lg font-medium" style={txtMain}>Actions</h2><p className="text-xs" style={txtSec}>Sauvegarder, générer et télécharger le DAL</p></div>
            </div>

            {statusMessagesEl}
            {actionMsg && (
              <div className="p-2.5 rounded-lg border text-xs" style={successStyle} data-testid="dal-action-msg">{actionMsg}</div>
            )}

            <Button
              onClick={async () => {
                setActionMsg(null); setSaveError(null);
                const r = await saveDAL();
                if (r.ok) setActionMsg("Enregistré ✓");
                else setSaveError(r);
              }}
              disabled={saving}
              variant="outline"
              className="w-full" style={btnDiscreet}
              data-testid="dal-action-sauver"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sauvegarde…</> : <><Save className="w-4 h-4 mr-2" /> Sauver</>}
            </Button>

            {genLocked && (
              <div className="rounded-lg border p-2.5 text-xs" style={errorStyle} data-testid="dal-generate-blocked">
                Génération bloquée — complétez d'abord : {genBlockers.join(", ")}.
              </div>
            )}
            <Button
              onClick={handleGenerate}
              disabled={saving || pdfGenerating || genLocked}
              className="w-full disabled:opacity-50" style={btnPrimary}
              data-testid="dal-action-generer"
            >
              {pdfGenerating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération du DAL…</>
                : <><FileText className="w-4 h-4 mr-2" /> Générer le DAL</>}
            </Button>

            <Button
              onClick={downloadDalPdf}
              disabled={!pdfResult?.download_url || pdfDownloading}
              variant="outline"
              className="w-full disabled:opacity-40" style={btnSecondary}
              data-testid="dal-action-telecharger"
            >
              {pdfDownloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Téléchargement…</> : <><Download className="w-4 h-4 mr-2" /> Télécharger</>}
            </Button>
            {!pdfResult?.download_url && (
              <p className="text-[11px]" style={txtMuted}>Le téléchargement sera disponible après génération du PDF.</p>
            )}
          </div>
        )}
      </main>
      </div>

      {/* Footer nav — MOBILE uniquement (stepper Préc./Suivant/Terminer). Desktop
          (embedded) : actions déplacées dans le groupe ACTIONS du menu latéral. */}
      {!embedded && (
      <footer className="fixed bottom-0 left-0 right-0 border-t p-4 z-40" style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}>
        {saveError && (
          <div className="mb-3 p-3 rounded-lg border text-xs" style={errorStyle} data-testid="mobile-dal-save-error">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium mb-1">Échec de l'enregistrement</p>
                <ul className="space-y-0.5 list-disc pl-4">
                  {saveError.errors.slice(0, 4).map((err, i) => (
                    <li key={i} className="truncate" title={err}>{err}</li>
                  ))}
                  {saveError.errors.length > 4 && (
                    <li className="opacity-80">+ {saveError.errors.length - 4} autres erreurs</li>
                  )}
                </ul>
                <p className="mt-1 opacity-80">
                  Photos : {saveError.stats.photos.ok}/{saveError.stats.photos.sent} OK ·
                  Docs : {saveError.stats.docs.ok}/{saveError.stats.docs.sent} OK ·
                  DAL : {saveError.stats.dal ? "✓" : "✗"}
                </p>
              </div>
              <button onClick={() => setSaveError(null)} className="p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {/* C1a — état génération PDF DAL (succès → bouton télécharger ; échec → message non bloquant) */}
        {pdfGenerating && (
          <div className="mb-3 p-3 rounded-lg border text-xs flex items-center gap-2" style={infoStyle} data-testid="mobile-dal-pdf-generating">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" /> Génération du PDF DAL en cours…
          </div>
        )}
        {pdfResult && !pdfGenerating && (
          <div className="mb-3 p-3 rounded-lg border text-xs flex items-center justify-between gap-2" style={successStyle} data-testid="mobile-dal-pdf-success">
            <span className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span className="truncate">PDF DAL généré{pdfResult.filename ? ` — ${pdfResult.filename}` : ""}</span>
            </span>
            <Button
              size="sm" variant="outline"
              onClick={downloadDalPdf}
              disabled={pdfDownloading}
              className="h-8 px-2 text-xs shrink-0" style={btnSecondary}
              data-testid="mobile-dal-pdf-download-btn"
            >
              {pdfDownloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
              Télécharger le DAL
            </Button>
          </div>
        )}
        {pdfError && !pdfGenerating && (
          <div className="mb-3 p-3 rounded-lg border text-xs flex items-start gap-2" style={infoStyle} data-testid="mobile-dal-pdf-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">DAL enregistré, mais la génération du PDF a échoué ({pdfError}). Vous pouvez réessayer.</span>
            <button onClick={generateDalPdf} className="underline shrink-0" data-testid="mobile-dal-pdf-retry">Réessayer</button>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={goPrev} disabled={isBesoinsStep} className="flex-1" style={btnDiscreet}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Préc.
          </Button>
          {isDocsStep ? (
            <Button onClick={handleFinish} disabled={saving} variant="outline" className="flex-1" style={btnDiscreet} data-testid="mobile-dal-finish-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sauvegarde...</> : <><Check className="w-4 h-4 mr-2" />Terminer</>}
            </Button>
          ) : (
            <Button onClick={goNext} variant="outline" className="flex-1" style={btnSecondary} data-testid="mobile-dal-next-btn">
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </footer>
      )}
    </div>
  );
}
